import { useLoader } from "@react-three/fiber";
import axios from "axios";
import MDButton from "components/MDButton";
import html2canvas from "html2canvas";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import MiniMap from "leaflet-minimap";
import { isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import { Tooltip, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CardHeader, CardTitle } from "reactstrap";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import { basicURL } from "../../apiManager/APIConfig";
import logo from "../../assets/images/logo.png";
import { openAppConfirm } from "../../components/AppDialogs";
import TranslateConfirmDialog from "../../components/TranslateConfirmDialog";
import ShowTileMapAndDialogs from "./showTile/ShowTileMapAndDialogs";
import {
  tileUrlPatternFromSlideFile,
  tileUrlPatternFromRoot,
  isUploadPhotoSlide,
  uploadPhotoLeafletView,
  fetchSlideTileManifest,
  slideTileLayerOptions,
  tileUrlWithExtension,
  slideImageLatLngBounds,
  verifySlideTileSource,
  assessUploadPhotoPyramid,
  resolveSlideMasterPreviewUrl,
  slideFileFromSlide,
} from "./showTile/tileUrl";
import ShowTileCaptureLoader from "./showTile/ShowTileCaptureLoader";
import { captureLeafletSnapshot } from "./showTile/captureLeafletSnapshot";
import RelatedModelsPanel from "./components/RelatedModelsPanel";
import { PathologyRoot } from "./pathology/PathologyRoot";
import PathologySidebarSection from "./pathology/PathologySidebarSection";
import { useWebPermissions } from "../../hooks/useWebPermissions";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import { askAIChatCall, getAIMessagesByThreadCall } from "../../redux/api/ai.api";
import {
  GetSlideByIdCall,
  GetLibraryVisibilityCall,
  UpdateLibraryVisibilityCall,
} from "../../redux/api/title.api";
import { DeleteFile } from "../../redux/api/upload.api";
import { translatePinsForSlideCall } from "../../redux/api/pinTranslation.api";
import { store } from "../../redux/store"; // token reads in callbacks
import "./map.css";

const PUBLIC_SLIDE_TYPES = ["his", "emb", "rad", "three_d_model", "emb_3d_model", "presentation"];
const CORE_PUBLIC_SLIDE_TYPES = ["his", "emb", "rad", "three_d_model", "emb_3d_model"];

function escapeHtml(text) {
  if (text == null || typeof text !== "string") return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function createSlidePinIcon() {
  // Use explicit asset URLs to avoid broken marker icons in webpack/runtime.
  return L.icon({
    iconRetinaUrl:
      "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });
}

/** User-facing copy for photo-tile job pipeline messages. */
function friendlyTileStatusMessage(message) {
  const raw = String(message || "").trim();
  if (!raw) return "Processing slide…";
  const lower = raw.toLowerCase();
  if (lower.startsWith("queued")) {
    return raw.replace(/\.\s*$/, "…");
  }
  if (
    lower.includes("preparing") ||
    lower.includes("saving") ||
    lower.includes("loading") ||
    lower.includes("master") ||
    lower.includes("resizing") ||
    lower.includes("image ok") ||
    lower.includes("starting tile")
  ) {
    return "Preparing slide image…";
  }
  if (lower.includes("generating tiles") || /tiles\s*\(\d+\/\d+\)/i.test(raw)) {
    return raw.replace(/\.\s*$/, "");
  }
  if (lower.includes("tiling image") || lower.includes("tiling complete") || lower.includes("writing tile")) {
    return lower.includes("complete") || lower.includes("writing")
      ? "Finishing zoom levels…"
      : "Building zoom levels…";
  }
  if (lower.includes("complete")) return "Opening slide…";
  return raw.replace(/:\s*$/, "");
}

function ShowTile({ history }) {
  const userData = history?.location?.state?.data?.array;
  const fileInputRef = useRef();
  const photoInputRef = useRef();
  const { id, lang } = useParams();
  const mapRef = useRef(null); // Ref for storing map instance
  const drawnItemsRef = useRef(null);
  const pendingNewLayerRef = useRef(null);
  const mapViewRef = useRef({
    center: [-10.505, -0.09],
    zoom: 2,
  });

  const content = userData?.content;

  const [info, setInfo] = useState([]);
  const [geoJson, setGeoJson] = useState([]);

  const [showLoader, setShowLoader] = useState(false);
  const [uploadProgress, setUploadPercentage] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState("uploadProgress:");

  const [drawerOpen, setOpenDrawer] = useState(false);
  const [activeDeleteButton, setActiveDeleteButton] = useState(false);
  const [tileURL, setTitleURL] = useState(``);
  const [key, setKey] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiThreadId, setAiThreadId] = useState(null);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [capturedImage, setCapturedImage] = useState("");
  const [hasDrawing, setHasDrawing] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  /** Upload-photo pyramid still building: show banner + temporary master preview. */
  const [tilingBuildNotice, setTilingBuildNotice] = useState(null);
  const masterOverlayRef = useRef(null);
  const [captureGridOpen, setCaptureGridOpen] = useState(false);
  const [gridRows, setGridRows] = useState(1);
  const [gridCols, setGridCols] = useState(1);
  const [scanDirection, setScanDirection] = useState("auto");
  const [currentTile, setCurrentTile] = useState(0);
  const [capturedTiles, setCapturedTiles] = useState({});
  const [captureSessionId, setCaptureSessionId] = useState("");
  const [captureFeedback, setCaptureFeedback] = useState("");
  const [captureBusy, setCaptureBusy] = useState(false);
  const [stitchBusy, setStitchBusy] = useState(false);
  const [stitchJobId, setStitchJobId] = useState("");
  const [stitchStatus, setStitchStatus] = useState("");
  const [stitchPipelinePercent, setStitchPipelinePercent] = useState(0);
  const [stitchPipelinePhase, setStitchPipelinePhase] = useState("");
  const [stitchPipelineMessage, setStitchPipelineMessage] = useState("");
  const [isCaptureSessionHydrated, setIsCaptureSessionHydrated] = useState(false);
  const [needsRetake, setNeedsRetake] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [initialGuideSeen, setInitialGuideSeen] = useState(false);
  const [studentPinsPanelOpen, setStudentPinsPanelOpen] = useState(false);
  const [studentsSeePublicPins, setStudentsSeePublicPins] = useState(false);
  const [translateAllPinsOpen, setTranslateAllPinsOpen] = useState(false);
  const [mapReadyVersion, setMapReadyVersion] = useState(0);
  const [ghostEnabled, setGhostEnabled] = useState(true);
  const [ghostOpacity, setGhostOpacity] = useState(35);
  const [referenceTile, setReferenceTile] = useState(null);
  const [referenceDirection, setReferenceDirection] = useState(null);
  const [referenceStripUrl, setReferenceStripUrl] = useState("");
  const [liveStripUrl, setLiveStripUrl] = useState("");
  const [overlapScore, setOverlapScore] = useState(null);
  const [overlapHint, setOverlapHint] = useState("");
  const [videoScanBusy, setVideoScanBusy] = useState(false);
  const [videoScanProgress, setVideoScanProgress] = useState(0);
  const [videoScanMessage, setVideoScanMessage] = useState("");
  const [scanMosaicPreviewUrl, setScanMosaicPreviewUrl] = useState("");
  const [scanAcceptedCount, setScanAcceptedCount] = useState(0);
  const [scanRejectedBlurCount, setScanRejectedBlurCount] = useState(0);
  const [scanRejectedMatchCount, setScanRejectedMatchCount] = useState(0);
  const [uploadPreparedBusy, setUploadPreparedBusy] = useState(false);
  const [uploadPreparedProgress, setUploadPreparedProgress] = useState(0);
  const [deleteUploadedBusy, setDeleteUploadedBusy] = useState(false);
  const videoRef = useRef(null);
  const captureViewportRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const fallbackCameraInputRef = useRef(null);
  const videoScanInputRef = useRef(null);
  const liveStripCanvasRef = useRef(null);
  const referenceStripDataRef = useRef(null);
  const captureSessionSaveTimerRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const baseImageRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const preparedTileBlobRef = useRef({});

  const role = useSelector((state) => state.auth?.role);
  const impersonatingUniversity = useSelector((state) => Boolean(state.impersonation?.active));
  const { isPlatformAdmin, isUniversityAdmin, canForSlideType } = useWebPermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Slides often hang under a subtitle row; core type (his/emb/3d) may live on the root title.
  const directTitleType =
    geoJson?.parentTitle?.type || geoJson?.Titles?.type || geoJson?.titles?.type || "";
  const rootTitleType = geoJson?.parentTitle?.parentTitle?.type || "";
  const apiCoreType = geoJson?.coreLibraryType;
  const resolvedPublicType = PUBLIC_SLIDE_TYPES.includes(directTitleType)
    ? directTitleType
    : PUBLIC_SLIDE_TYPES.includes(rootTitleType)
      ? rootTitleType
      : directTitleType;
  const resolvedCoreType = CORE_PUBLIC_SLIDE_TYPES.includes(String(apiCoreType || ""))
    ? apiCoreType
    : CORE_PUBLIC_SLIDE_TYPES.includes(directTitleType)
      ? directTitleType
      : CORE_PUBLIC_SLIDE_TYPES.includes(rootTitleType)
        ? rootTitleType
        : "";
  const isPublicSlide = PUBLIC_SLIDE_TYPES.includes(resolvedPublicType);
  const isCorePublicSlide = CORE_PUBLIC_SLIDE_TYPES.includes(resolvedCoreType);
  // True for Microlab catalogue slides (his/emb/…) including when linked into a
  // private library and opened with ?private=1. coreLibraryType is the reliable
  // signal from the API for linked public content.
  const isMicrolabSourcedSlide = isPublicSlide || isCorePublicSlide;
  const slideMetaReady = Boolean(
    geoJson && (geoJson.id || geoJson.parentTitle || geoJson.Files || geoJson.coreLibraryType)
  );
  const canEditSlideContent =
    isPlatformAdmin || canForSlideType(resolvedCoreType, "slides_edit");
  const canEditPinsOnSlide =
    isPlatformAdmin || canForSlideType(resolvedCoreType, "pins_edit");
  const isPrivateView =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("private") === "1";
  const roleKeyEarly = String(role || "").toLowerCase();
  // Upload/delete media only for user-owned private titles.
  // Never for Microlab public/library slides — even uni_admin has slides_edit
  // on catalogue types, which must not unlock Delete/Upload on linked content.
  const canManagePrivateLibrarySlide =
    slideMetaReady &&
    isPrivateView &&
    !isMicrolabSourcedSlide &&
    roleKeyEarly !== "student" &&
    ["prof", "professor", "university_admin", "admin"].includes(roleKeyEarly);
  const canUploadPhotoOnSlide =
    canManagePrivateLibrarySlide ||
    (slideMetaReady &&
      isPlatformAdmin &&
      canEditSlideContent &&
      !(isPrivateView && isMicrolabSourcedSlide));
  const canDeletePhotoOnSlide = canUploadPhotoOnSlide;
  const canEditSlide =
    canManagePrivateLibrarySlide ||
    (slideMetaReady &&
      isPlatformAdmin &&
      canEditSlideContent &&
      !(isPrivateView && isMicrolabSourcedSlide));
  const canEditPins =
    role !== "student" &&
    (isPrivateView || !isPublicSlide || canEditPinsOnSlide);
  const showTranslateAllPins =
    canEditPins &&
    !["university_admin", "prof", "professor"].includes(roleKeyEarly) &&
    (resolvedCoreType === "his" ||
      resolvedCoreType === "emb" ||
      resolvedCoreType === "rad");
  const persistedPinCount = Array.isArray(geoJson?.pins) ? geoJson.pins.length : 0;
  const sourceTitleId =
    geoJson?.parentTitle?.id || geoJson?.Titles?.id || geoJson?.titles?.id || null;
  ////3d modles

  useEffect(() => {
    fetchSlide();
  }, []);
  /////////
  /////////
  function generateUniqueId() {
    return "id-" + Math.random().toString(36).substr(2, 16);
  }
  const fetchSlide = async () => {
    const params = new URLSearchParams(window.location.search);
    const includePrivatePins = params.get("private") === "1";
    const res = await GetSlideByIdCall({
      id,
      lang,
      includePrivatePins,
      ts: Date.now(),
    });
    ////here ......
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    setGeoJson(res);

    setActiveDeleteButton(res?.Files?.[0]?.url ? true : false);
    setTitleURL(tileUrlPatternFromSlideFile(res?.Files?.[0]));
    // window.location.reload();
  };
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      if (!isEmpty(geoJson) && tileURL && !mapRef.current) {
        const mapContainer = L.DomUtil.get("map");
        if (mapContainer && mapContainer._leaflet_id) {
          // Defensive cleanup for hot reload / rapid state updates.
          mapContainer._leaflet_id = null;
        }
        const slideMaxZoom =
          typeof geoJson?.max_zoom === "number" && geoJson.max_zoom >= 0
            ? geoJson.max_zoom
            : undefined;
        const uploadPhoto = isUploadPhotoSlide(geoJson);
        const rawManifest = await fetchSlideTileManifest(tileURL);
        if (cancelled) return;
        const { manifest, tileExt: detectedExt } = await verifySlideTileSource(
          tileURL,
          rawManifest
        );
        if (cancelled) return;

        let effectiveMaxZoom = slideMaxZoom;
        let pyramidBuilding = false;
        if (uploadPhoto) {
          const assessment = await assessUploadPhotoPyramid(tileURL, slideMaxZoom);
          if (cancelled) return;
          if (assessment.maxNativeZoom != null) {
            effectiveMaxZoom = assessment.maxNativeZoom;
          }
          pyramidBuilding = Boolean(assessment.building);
          if (pyramidBuilding) {
            setTilingBuildNotice({
              message:
                "Building zoom levels… preview available; full zoom unlocks when tiling finishes.",
            });
          } else {
            setTilingBuildNotice(null);
          }
        } else {
          setTilingBuildNotice(null);
        }

        const pyramid = slideTileLayerOptions(manifest, effectiveMaxZoom);
        const layerUrl = tileUrlWithExtension(tileURL, pyramid?.tileExt || detectedExt);
        const worldBounds = [
          [-90, -180],
          [90, 180],
        ];
        const zoomLimits = {
          ...(pyramid?.minZoom != null ? { minZoom: pyramid.minZoom } : {}),
          ...(pyramid?.maxNativeZoom != null ? { maxNativeZoom: pyramid.maxNativeZoom } : {}),
          ...(pyramid?.maxZoom != null ? { maxZoom: pyramid.maxZoom } : {}),
          ...(pyramid?.tileSize ? { tileSize: pyramid.tileSize } : {}),
        };

        const map = L.map("map", {
          center: mapViewRef.current.center,
          zoom: mapViewRef.current.zoom,
          maxBounds: worldBounds,
          maxBoundsViscosity: 1,
          ...(pyramid?.minZoom != null ? { minZoom: pyramid.minZoom } : {}),
          ...(pyramid?.maxZoom != null ? { maxZoom: pyramid.maxZoom } : {}),
        });
        if (cancelled) {
          map.remove();
          return;
        }
        mapRef.current = map;
        setMapReadyVersion((v) => v + 1);

        const nativeZoom = pyramid?.maxNativeZoom ?? slideMaxZoom;
        const imageBounds = slideImageLatLngBounds(map, manifest);
        const tileBounds = imageBounds || worldBounds;
        if (imageBounds) {
          map.setMaxBounds(imageBounds.pad(0.05));
          /*
            Fit the whole slide into the viewport. Without this, the map
            stays on the default world center and the image appears as a
            thin strip in a sea of white empty tiles.
          */
          map.fitBounds(imageBounds, { animate: false, padding: [24, 24] });
          const center = map.getCenter();
          mapViewRef.current = {
            center: [center.lat, center.lng],
            zoom: map.getZoom(),
          };
        } else if (uploadPhoto && nativeZoom != null) {
          /*
            tiles.json missing/blocked: leave the default world view and
            frame the top-left of the pyramid so tiles aren't a speck.
          */
          const z = Math.min(2, Math.max(0, nativeZoom));
          const approx = L.latLngBounds(
            map.unproject([0, 256 * 8], z),
            map.unproject([256 * 32, 0], z)
          );
          if (approx.isValid()) {
            map.setMaxBounds(approx.pad(0.15));
            map.fitBounds(approx, { animate: false, padding: [24, 24] });
            const center = map.getCenter();
            mapViewRef.current = {
              center: [center.lat, center.lng],
              zoom: map.getZoom(),
            };
          } else {
            const photoView = uploadPhotoLeafletView(map, nativeZoom);
            map.setView(photoView.center, photoView.zoom);
            mapViewRef.current = { center: photoView.center, zoom: photoView.zoom };
          }
        }

        map.on("moveend zoomend", () => {
          const center = map.getCenter();
          mapViewRef.current = {
            center: [center.lat, center.lng],
            zoom: map.getZoom(),
          };
        });
        const mainTileLayer = L.tileLayer(layerUrl, {
          attribution: '&copy; <a href="https://microlab.ink">Microlab</a> contributors',
          noWrap: true,
          tms: false,
          bounds: tileBounds,
          // Required so Ask AI / html2canvas can read tile pixels (CORS).
          crossOrigin: true,
          ...zoomLimits,
        }).addTo(map);
        mainTileLayer.on("tileerror", (event) => {
          console.warn("Slide tile failed to load:", event?.tile?.src || event?.coords);
        });
        // Fix first-render blank main map by forcing Leaflet size recalculation.
        // Do not reset map view here — "load" fires on every zoom and snapped users back to the start.
        mainTileLayer.on("load", () => {
          setTimeout(() => map.invalidateSize(true), 0);
          setTimeout(() => map.invalidateSize(true), 300);
        });

        /*
          While the deep-zoom pyramid is still uploading, show the master
          JPEG so the user sees the slide instead of a white / postage-stamp map.
        */
        if (masterOverlayRef.current) {
          try {
            map.removeLayer(masterOverlayRef.current);
          } catch (_) {
            /* ignore */
          }
          masterOverlayRef.current = null;
        }
        if (uploadPhoto && pyramidBuilding) {
          const masterUrl = await resolveSlideMasterPreviewUrl(slideFileFromSlide(geoJson));
          if (!cancelled && masterUrl) {
            const overlayBounds = imageBounds || (tileBounds?.isValid ? tileBounds : null);
            let boundsForOverlay = overlayBounds;
            if (!boundsForOverlay || !boundsForOverlay.isValid?.()) {
              const z = Math.min(2, Math.max(0, nativeZoom ?? 1));
              boundsForOverlay = L.latLngBounds(
                map.unproject([0, 256 * 8], z),
                map.unproject([256 * 32, 0], z)
              );
            }
            if (boundsForOverlay?.isValid?.()) {
              const overlay = L.imageOverlay(masterUrl, boundsForOverlay, {
                opacity: 1,
                interactive: false,
                zIndex: 200,
                crossOrigin: true,
              }).addTo(map);
              masterOverlayRef.current = overlay;
            }
          }
        }
        //////////
        const minimapLayer = L.tileLayer(layerUrl, {
          noWrap: true,
          tms: false,
          bounds: tileBounds,
          crossOrigin: true,
          ...zoomLimits,
        });
        const overviewZoom =
          pyramid?.minZoom != null
            ? pyramid.minZoom
            : 0;
        ////////
        ////////
        new MiniMap(minimapLayer, {
          zoomLevelFixed: overviewZoom,
          position: "bottomleft",
          width: 120,
          height: 120,
          collapsedWidth: 5,
          collapsedHeight: 5,
          aimingRectOptions: {
            color: "#00f",
            weight: 1,
            fillOpacity: 0.1,
          },
        }).addTo(map);
        if (pyramid?.maxZoom != null) map.setMaxZoom(pyramid.maxZoom);
        if (pyramid?.minZoom != null) map.setMinZoom(pyramid.minZoom);
        setTimeout(() => map.invalidateSize(true), 0);
        setTimeout(() => map.invalidateSize(true), 500);

        /////////
        ///////// show the saved pins

        const isStudent = role === "student";
        const params = new URLSearchParams(window.location.search);
        const isPrivateView = params.get("private") === "1";
        const canEditPinsLocal =
          !isStudent &&
          (isPrivateView || !isPublicSlide || canEditPinsOnSlide);

        (geoJson?.pins || []).forEach((pin, index) => {
          const location = JSON.parse(pin.locations);
          const coordinates = location.data.geometry.coordinates;
          const marker = L.marker([coordinates[1], coordinates[0]], { icon: createSlidePinIcon() }).addTo(map);

          const titleLocalization = pin.pinLocalizations?.find(
            (loc) => loc.field === "title" && loc.language === lang
          );
          const bodyLocalization = pin.pinLocalizations?.find(
            (loc) => loc.field === "body" && loc.language === lang
          );
          const pinTitle = titleLocalization?.value || "Pin";
          const pinBody = bodyLocalization?.value || "";

          if (!canEditPinsLocal) {
            // Read-only: show pin details in a popup with close button
            const popupContent = `<div class="pin-popup-content" style="min-width:180px;max-width:280px;">
              <div style="font-weight:bold;margin-bottom:6px;font-size:14px;">${escapeHtml(pinTitle)}</div>
              ${pinBody ? `<div style="font-size:13px;color:#444;line-height:1.4;">${escapeHtml(pinBody)}</div>` : ""}
            </div>`;
            marker.bindPopup(popupContent, { closeButton: true });
          } else {
            marker.on("click", () => {
              const updatedPin = {
                ...pin,
                title: pinTitle,
                body: pinBody,
              };
              setInfo(updatedPin);
              setTimeout(() => toggleDrawer(true), 200);
            });
          }
        });
        /////////
        /////////
        // FeatureGroup and draw toolbar: only when pins are editable (students & read-only views see no toolbar)
        var drawnItems;
        if (canEditPins) {
          const existingCount = Array.isArray(geoJson?.pins) ? geoJson.pins.length : 0;
          drawnItems = new L.FeatureGroup();
          map.addLayer(drawnItems);
          drawnItemsRef.current = drawnItems;
          var drawControl = new L.Control.Draw({
            position: "topright",
            draw: {
              // Use a custom icon even while placing the marker to avoid the broken default icon
              marker: {
                icon: createSlidePinIcon(),
              },
              polygon: false,
              polyline: false,
              rectangle: false,
              circle: false,
              circlemarker: false,
            },
            edit: {
              featureGroup: drawnItems,
              edit: false,
              remove: true, // keep only the bin (delete) action
            },
          });
          map.addControl(drawControl);
        }
        if (!isEmpty(geoJson?.pins)) {
          let geoJsonPinIndex = 0;
          L.geoJSON(geoJson?.pins, {
            pointToLayer: (feature, latlng) => {
              geoJsonPinIndex++;
              return L.marker(latlng, { icon: createSlidePinIcon() });
            },
          })
            .addTo(map)
            .on(
              "click",
              function (e) {
                if (!canEditPins) return;
                const { layer } = e;

                const id = layer?.feature.properties.id;
                setInfo({ content: content[id], markerID: id });

                setTimeout(() => {
                  toggleDrawer(true);
                }, 200);
              }.bind()
            );
        }
        ///////////////
        ///////////////
        ///////////////
        const watermark = L.control({ position: "bottomright" });
        watermark.onAdd = function (map) {
          const div = L.DomUtil.create("div", "watermark");
          div.style.background = "transparent"; // Ensure background is transparent
          div.innerHTML = `<img src=${logo} alt="Microlab Watermark" style="width: 100px; height: auto;">`;
          return div;
        };
        watermark.addTo(map);
        ///////////////
        ///////////////
        ///////////////
        ////Edit and add functions (only when pins are editable)
        if (canEditPins && drawnItems) {
          map.on("draw:created", function (e) {
            var type = e.layerType,
              layer = e.layer;

            console.log("layer draw:created : ", layer);
            if (type === "marker") {
              // showModal(layer);
              // Do marker specific actions
              const latlng = layer.getLatLng(); // Get latitude and longitude of the marker

              // Use the same visual pin style as existing pins
              layer.setIcon(createSlidePinIcon());

              // Construct GeoJSON structure
              const newFeature = {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [latlng.lng, latlng.lat], // Leaflet uses lat/lng; GeoJSON uses lng/lat
                },
                properties: {
                  id: generateUniqueId(), // Replace with your logic to generate an ID
                },
              };

              setInfo(newFeature);
              pendingNewLayerRef.current = layer;
              // Open dialog immediately for new pin
              setTimeout(() => {
                toggleDrawer(true);
              }, 0);
            }
            layer.on("click", (e) => {
              toggleDrawer(true);
            });
            ///////////////
            ///////////////
            ///////////////
            // Do whatever else you need to. (save to db; add to map etc)
            drawnItems.addLayer(layer);
          });

          map.addLayer(drawnItems);
        }
        ///////////////
        ///////////////
        ///////////////


      }
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (masterOverlayRef.current) {
        try {
          masterOverlayRef.current.remove();
        } catch (_) {
          /* ignore */
        }
        masterOverlayRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      drawnItemsRef.current = null;
      pendingNewLayerRef.current = null;
    };
  }, [tileURL, geoJson, lang]);

  useEffect(() => {
    if (tilingBuildNotice) return undefined;
    if (masterOverlayRef.current) {
      try {
        masterOverlayRef.current.remove();
      } catch (_) {
        /* ignore */
      }
      masterOverlayRef.current = null;
    }
    return undefined;
  }, [tilingBuildNotice]);

  const tilingBuildActive = Boolean(tilingBuildNotice);
  /*
    Keep checking until tiles.json (full pyramid) appears, then remount
    the map with correct zoom metadata. Caps after ~3 minutes so legacy
    slides without a manifest are not polled forever.
  */
  useEffect(() => {
    if (!tilingBuildActive || !tileURL || !isUploadPhotoSlide(geoJson)) return undefined;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60;
    const interval = setInterval(async () => {
      attempts += 1;
      try {
        const slideMaxZoom =
          typeof geoJson?.max_zoom === "number" && geoJson.max_zoom >= 0
            ? geoJson.max_zoom
            : undefined;
        const assessment = await assessUploadPhotoPyramid(tileURL, slideMaxZoom);
        if (cancelled) return;
        if (!assessment.building && (assessment.ready || assessment.manifest)) {
          setTilingBuildNotice(null);
          await fetchSlide();
          return;
        }
      } catch (err) {
        console.warn("Tile readiness poll failed:", err?.message || err);
      }
      if (attempts >= maxAttempts) {
        setTilingBuildNotice((prev) =>
          prev
            ? {
                ...prev,
                message:
                  "Zoom levels may still be limited. Refresh the page if the image looks incomplete.",
              }
            : null
        );
        clearInterval(interval);
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [tilingBuildActive, tileURL, geoJson]);

  const handleAddMaterial = async (event) => {
    if (activeDeleteButton) {
      const file_id = geoJson?.Files?.[0]?.id;
      const confirmation = await openAppConfirm({
        title: "Delete slide?",
        message: "Are you sure you want to delete this slide? Related pins will be removed as well.",
        confirmText: "Delete",
        cancelText: "Cancel",
        confirmColor: "error",
      });
      if (confirmation) {
        try {
          const delRes = await DeleteFile(file_id);
          if (delRes?.status === 200) {
            setGeoJson((prev) => ({ ...(prev || {}), pins: [], Files: [] }));
            setActiveDeleteButton(false);
            setTitleURL("");
            setToast({
              open: true,
              severity: "success",
              message: "Slide and related pins deleted successfully.",
            });
            await fetchSlide();
            return;
          }
          setToast({
            open: true,
            severity: "error",
            message: delRes?.message || "Delete failed. Please try again.",
          });
        } catch (err) {
          setToast({
            open: true,
            severity: "error",
            message: err?.message || "Delete failed. Please try again.",
          });
        }
      }
    } else {
      const file = event.target.files[0];
      if (file) {
        try {
          setShowLoader(true);
          setUploadPercentage(0);
          setUploadStatusText("Uploading ZIP:");
          // Handle `.zip` file upload
          const formdata = new FormData();
          formdata.append("file", file);
          formdata.append("entity_type", "slide");
          formdata.append("entity_id", id);
          formdata.append("destination", "slides");
          formdata.append("folder_name", "slides");

          const access_token = store.getState().auth?.access_token;
          let myHeaders = new Headers();
          myHeaders.append("Authorization", access_token);

          myHeaders = {
            Authorization: access_token,
          };

          const response = await axios.post(`${basicURL}file/upload_lambda`, formdata, {
            headers: myHeaders,
            onUploadProgress: (progressEvent) => {
              const raw = Math.round((progressEvent.loaded / progressEvent.total) * 100);
              const uploadWeighted = Math.round(raw * 0.7); // reserve last 30% for extraction phase
              setUploadPercentage(uploadWeighted);
            },
          });
          console.log("pippo response", response);
          const jobId = response?.data?.jobId;
          if (!jobId) {
            setShowLoader(false);
            setToast({
              open: true,
              severity: "warning",
              message: "Upload started, but tracking id is missing.",
            });
            return;
          }

          setUploadStatusText("Extracting ZIP on server:");
          const maxPolls = 450; // up to 15 minutes with 2s interval
          for (let i = 0; i < maxPolls; i++) {
            const statusRes = await axios.get(`${basicURL}file/upload_lambda/job/${jobId}`, {
              headers: myHeaders,
            });
            const extractPercent = Number(statusRes?.data?.extractPercent || 0);
            const uiPercent = Math.min(100, 70 + Math.round(extractPercent * 0.3));
            setUploadPercentage(uiPercent);

            if (statusRes?.data?.jobStatus === "complete") {
              setUploadPercentage(100);
              setShowLoader(false);
              const tileRootUrl =
                statusRes?.data?.result?.tile_root_url || response?.data?.tile_root_url;
              if (tileRootUrl) {
                setTitleURL(tileUrlPatternFromRoot(tileRootUrl, "png"));
              }
              await fetchSlide();
              setToast({
                open: true,
                severity: "success",
                message: "Slide uploaded and extracted successfully.",
              });
              return;
            }

            if (statusRes?.data?.jobStatus === "error") {
              throw new Error(statusRes?.data?.error || "Extraction failed.");
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
          }

          throw new Error("Extraction timed out. Please check server logs.");
        } catch (error) {
          console.error("Upload error:", error);
          setShowLoader(false);
          setUploadStatusText("uploadProgress:");
          setToast({
            open: true,
            severity: "error",
            message: error?.message || "Upload or extraction failed.",
          });
        }
      }
    }
  };

  const pollSlideTileJob = async (jobId, { onProgress } = {}) => {
    const access_token = store.getState().auth?.access_token;
    const headers = { Authorization: access_token };
    const maxPolls = 450;
    for (let i = 0; i < maxPolls; i += 1) {
      const statusRes = await axios.get(
        `${basicURL}file/slides/${id}/capture-session/stitch/${jobId}`,
        {
          headers,
          // Cache-bust only via query param — a Cache-Control request header triggers
          // CORS preflight and fails if the API/nginx omits it from Allow-Headers.
          params: { _: Date.now() },
        }
      );
      const pipeline = statusRes?.data?.pipeline || {};
      const percent =
        typeof pipeline?.percent === "number"
          ? Math.max(0, Math.min(100, pipeline.percent))
          : 0;
      onProgress?.(percent, pipeline?.message || statusRes?.data?.message);
      if (statusRes?.data?.jobStatus === "complete") {
        return statusRes;
      }
      if (statusRes?.data?.jobStatus === "error") {
        throw new Error(
          statusRes?.data?.error ||
            pipeline?.message ||
            statusRes?.data?.message ||
            "Tiling failed."
        );
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    throw new Error("Tiling timed out. Please check server logs.");
  };

  const handleUploadPhoto = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    event.target.value = "";
    const lowerName = String(file.name || "").toLowerCase();
    if (!/\.(png|jpe?g|tiff?)$/.test(lowerName)) {
      setToast({
        open: true,
        severity: "error",
        message: "Only PNG, JPG, or TIF images are supported.",
      });
      return;
    }

    try {
      setShowLoader(true);
      setUploadPercentage(0);
      setUploadStatusText("Uploading photo:");
      const formdata = new FormData();
      formdata.append("file", file);
      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };
      const privateQs = isPrivateView ? "?private=1" : "";
      const response = await axios.post(
        `${basicURL}file/slides/${id}/upload-photo${privateQs}`,
        formdata,
        {
        headers,
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 1;
          const raw = Math.round((progressEvent.loaded / total) * 100);
          setUploadPercentage(Math.min(28, Math.round(raw * 0.28)));
        },
      });
      const jobId = response?.data?.jobId;
      if (!jobId) {
        throw new Error("Upload started, but tracking id is missing.");
      }

      setUploadStatusText("Building zoom levels…");
      await pollSlideTileJob(jobId, {
        onProgress: (pct, message) => {
          setUploadPercentage(Math.min(99, 28 + Math.round(pct * 0.72)));
          setUploadStatusText(friendlyTileStatusMessage(message));
        },
      });

      setUploadStatusText("Opening slide…");
      setUploadPercentage(100);
      await fetchSlide();
      setShowLoader(false);
      setTilingBuildNotice(null);
      setToast({
        open: true,
        severity: "success",
        message: "Photo uploaded and tiled successfully.",
      });
    } catch (error) {
      console.error("Photo upload error:", error);
      setShowLoader(false);
      setUploadStatusText("uploadProgress:");
      setToast({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || error?.message || "Photo upload failed.",
      });
    }
  };

  const toggleDrawer = (open, cancelNew = false) => {
    if (!open && cancelNew && pendingNewLayerRef.current && drawnItemsRef.current) {
      try {
        drawnItemsRef.current.removeLayer(pendingNewLayerRef.current);
      } catch (e) {
        // ignore if already removed
      }
      pendingNewLayerRef.current = null;
    }
    setOpenDrawer(open);
  };
  const uploaderTitle = uploadStatusText;

  const isStudent = role === "student";

  const getSlideContextTitle = () => {
    // Private library linked slides: prefer the name from our library subtitle.
    const libraryTitle =
      (typeof geoJson?.libraryDisplayTitle === "string" &&
        geoJson.libraryDisplayTitle.trim()) ||
      "";
    if (libraryTitle) return libraryTitle;

    const slideLocs = Array.isArray(geoJson?.slideLocalizations) ? geoJson.slideLocalizations : [];
    const subtitleLocs = Array.isArray(geoJson?.titleLocalizations) ? geoJson.titleLocalizations : [];
    const parentTitleLocs = Array.isArray(geoJson?.Titles?.titleLocalizations)
      ? geoJson.Titles.titleLocalizations
      : [];

    const pickLocalizedTitle = (locs) =>
      locs.find((loc) => loc?.field === "title" && loc?.language === lang)?.value ||
      locs.find((loc) => loc?.field === "title" && loc?.language === "en")?.value ||
      locs.find((loc) => loc?.field === "title")?.value;

    const localized =
      pickLocalizedTitle(slideLocs) ||
      pickLocalizedTitle(subtitleLocs) ||
      pickLocalizedTitle(parentTitleLocs);

    return (
      localized ||
      geoJson?.name ||
      geoJson?.parentTitle?.name ||
      geoJson?.Titles?.name ||
      geoJson?.titles?.name ||
      "histology"
    );
  };

  const setupDrawingCanvas = () => {
    const image = baseImageRef.current;
    const canvas = drawCanvasRef.current;
    if (!image || !canvas) return;
    const rect = image.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width));
    canvas.height = Math.max(1, Math.floor(rect.height));
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#ef4444";
  };

  const getRelativePoint = (event) => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const onDrawStart = (event) => {
    event.preventDefault();
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const point = getRelativePoint(event);
    drawingRef.current = true;
    lastPointRef.current = point;
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
  };

  const onDrawMove = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const point = getRelativePoint(event);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    setHasDrawing(true);
  };

  const onDrawEnd = () => {
    drawingRef.current = false;
  };

  const clearDrawing = () => {
    const canvas = drawCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawing(false);
  };

  const dataUrlToFile = async (dataUrl, fileName) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type || "image/png" });
  };

  const buildAnnotatedImage = async () => {
    const base = baseImageRef.current;
    const drawCanvas = drawCanvasRef.current;
    if (!base || !drawCanvas) return null;

    const output = document.createElement("canvas");
    output.width = drawCanvas.width;
    output.height = drawCanvas.height;
    const ctx = output.getContext("2d");
    ctx.drawImage(base, 0, 0, output.width, output.height);
    ctx.drawImage(drawCanvas, 0, 0, output.width, output.height);
    return output.toDataURL("image/png");
  };

  const openAiDialog = async () => {
    setAiError("");
    setCaptureLoading(true);
    setAiOpen(true);
    try {
      const mapNode = document.getElementById("map");
      if (!mapNode) throw new Error("Map not found");
      const dataUrl = await captureLeafletSnapshot({
        map: mapRef.current,
        mapNode,
        html2canvas,
      });
      if (!dataUrl) throw new Error("Empty snapshot");
      setCapturedImage(dataUrl);
      setHasDrawing(false);
      setTimeout(setupDrawingCanvas, 0);
      if (aiThreadId) {
        const historyRes = await getAIMessagesByThreadCall(aiThreadId);
        const normalized = Array.isArray(historyRes)
          ? historyRes
              .map((m) => ({
                sender: m?.sender === "ai" ? "ai" : "user",
                content: m?.content || "",
              }))
              .filter((m) => !!m.content)
          : [];
        setAiMessages(normalized);
      } else {
        setAiMessages([]);
      }
    } catch (error) {
      console.error("Ask AI slide capture failed:", error);
      setAiError("Could not capture slide snapshot. Please try again.");
      setCapturedImage("");
    } finally {
      setCaptureLoading(false);
    }
  };

  const sendAiQuestion = async () => {
    const question = aiQuestion.trim();
    if (!question || aiLoading) return;
    setAiLoading(true);
    setAiError("");
    const pendingUserMessage = { sender: "user", content: question };
    setAiMessages((prev) => [...prev, pendingUserMessage]);
    setAiQuestion("");

    try {
      const formData = new FormData();
      formData.append("body", question);
      formData.append("title", getSlideContextTitle());
      formData.append("type", "ai");
      formData.append("entity_type", "aiQuestion");
      formData.append("slide_id", id);
      if (resolvedCoreType) {
        formData.append("slide_type", resolvedCoreType);
      }
      const slideSubject =
        resolvedCoreType === "emb"
          ? "embryology"
          : resolvedCoreType === "rad"
            ? "radiology"
          : resolvedCoreType === "his"
            ? "histology"
            : "";
      if (slideSubject) {
        formData.append("subject", slideSubject);
      }
      if (aiThreadId) {
        formData.append("thread_id", aiThreadId);
      }

      const annotated = await buildAnnotatedImage();
      if (annotated) {
        const file = await dataUrlToFile(annotated, `slide-${id}-annotation.png`);
        formData.append("file", file);
      }

      const res = await askAIChatCall(formData);
      if (res?.thread_id && !aiThreadId) {
        setAiThreadId(res.thread_id);
      }
      const aiContent = res?.messages?.[1]?.content?.trim();
      if (aiContent) {
        setAiMessages((prev) => [...prev, { sender: "ai", content: aiContent }]);
      } else {
        setAiMessages((prev) => [
          ...prev,
          { sender: "ai", content: "Sorry, I could not generate a response. Please try again." },
        ]);
      }
    } catch (error) {
      setAiMessages((prev) => [
        ...prev,
        { sender: "ai", content: "Sorry, request failed. Please try again." },
      ]);
      setAiError("Failed to send question to AI.");
    } finally {
      setAiLoading(false);
      setTimeout(() => {
        if (chatMessagesRef.current) {
          chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
      }, 0);
    }
  };

  useEffect(() => {
    if (!capturedImage) return;
    const onResize = () => setupDrawingCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [capturedImage]);

  const totalTiles = Math.max(1, Number(gridRows) * Number(gridCols));
  const capturedCount = Object.values(capturedTiles || {}).filter((t) => t?.done).length;
  const preparedCount = Object.values(capturedTiles || {}).filter((t) => t?.status === "prepared")
    .length;
  const capturePercent = Math.round((capturedCount / totalTiles) * 100);
  const currentRow = Math.floor(currentTile / gridCols);
  const currentCol = currentTile % gridCols;
  const currentTileKey = `${currentRow}-${currentCol}`;

  const classifyOverlap = (score) => {
    if (score == null) return { label: "", severity: "info" };
    if (score >= 75) return { label: "Good alignment", severity: "success" };
    if (score >= 55) return { label: "Acceptable, refine a bit", severity: "warning" };
    return { label: "Risk of gap/shift, realign before capture", severity: "error" };
  };

  const toGray = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;

  const buildGrayStrip = (imgData, w, h) => {
    const out = new Float32Array(w * h);
    const src = imgData.data;
    for (let i = 0, p = 0; i < src.length; i += 4, p += 1) {
      out[p] = toGray(src[i], src[i + 1], src[i + 2]);
    }
    return out;
  };

  const compareGrayStrips = (a, b) => {
    if (!a || !b || a.length !== b.length || a.length === 0) return null;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) diff += Math.abs(a[i] - b[i]);
    const meanDiff = diff / a.length;
    return Math.max(0, Math.min(100, Math.round(100 - (meanDiff / 255) * 100)));
  };

  const getReferenceInfoForCurrentTile = () => {
    const leftKey = `${currentRow}-${currentCol - 1}`;
    const topKey = `${currentRow - 1}-${currentCol}`;
    if (currentCol > 0 && capturedTiles[leftKey]) {
      return { tile: capturedTiles[leftKey], direction: "horizontal" };
    }
    if (currentRow > 0 && capturedTiles[topKey]) {
      return { tile: capturedTiles[topKey], direction: "vertical" };
    }
    return { tile: null, direction: null };
  };

  const extractStripFromImage = async (imageUrl, direction) => {
    if (!imageUrl || !direction) return { stripUrl: "", stripGray: null };
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return { stripUrl: "", stripGray: null };
    const canvas = document.createElement("canvas");
    const strip = 80;
    if (direction === "horizontal") {
      canvas.width = strip;
      canvas.height = h;
    } else {
      canvas.width = w;
      canvas.height = strip;
    }
    const ctx = canvas.getContext("2d");
    if (direction === "horizontal") {
      ctx.drawImage(img, w - strip, 0, strip, h, 0, 0, strip, h);
    } else {
      ctx.drawImage(img, 0, h - strip, w, strip, 0, 0, w, strip);
    }
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return {
      stripUrl: canvas.toDataURL("image/jpeg", 0.85),
      stripGray: buildGrayStrip(imgData, canvas.width, canvas.height),
    };
  };

  const stopCaptureCamera = () => {
    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    } catch (_) {
      // ignore
    }
    mediaStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  };

  const startCaptureCamera = async () => {
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setCaptureFeedback(
          "Camera API unavailable in this browser/context. Use HTTPS (or localhost) and a modern browser."
        );
        setToast({
          open: true,
          severity: "error",
          message: "Camera is not available in this browser/context.",
        });
        return false;
      }
      if (!window.isSecureContext) {
        setCaptureFeedback(
          "HTTP mode detected. Live preview is blocked on insecure HTTP; use fallback capture button."
        );
        setToast({
          open: true,
          severity: "info",
          message: "Using fallback capture on HTTP (no live preview).",
        });
        return false;
      }

      stopCaptureCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
      setCaptureFeedback("Camera ready. Align microscope eyepiece and capture current tile.");
      return true;
    } catch (error) {
      const msg = String(error?.message || "");
      const denied =
        error?.name === "NotAllowedError" ||
        error?.name === "SecurityError" ||
        /denied|permission/i.test(msg);
      const notFound = error?.name === "NotFoundError" || /not found|no camera/i.test(msg);
      const detail = denied
        ? "Camera permission denied. Allow camera access in browser settings."
        : notFound
          ? "No camera device was found."
          : "Camera access failed. Please allow camera permission and retry.";
      setCaptureFeedback(detail);
      setToast({
        open: true,
        severity: "error",
        message: detail,
      });
      return false;
    }
  };

  const computeBlurScore = (ctx, w, h) => {
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;
    const gray = new Float32Array(w * h);
    for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
      gray[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    let sum = 0;
    let sumSq = 0;
    let n = 0;
    for (let y = 1; y < h - 1; y += 1) {
      for (let x = 1; x < w - 1; x += 1) {
        const i = y * w + x;
        const lap = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - w] - gray[i + w];
        sum += lap;
        sumSq += lap * lap;
        n += 1;
      }
    }
    if (n === 0) return 0;
    const mean = sum / n;
    return Math.max(0, (sumSq / n) - mean * mean);
  };

  const fetchCaptureSessionConfig = async () => {
    const access_token = store.getState().auth?.access_token;
    const headers = { Authorization: access_token };
    const res = await axios.get(`${basicURL}file/slides/${id}/capture-session`, { headers });
    return res?.data?.data || null;
  };

  const saveCaptureSessionConfig = async (payload) => {
    const access_token = store.getState().auth?.access_token;
    const headers = { Authorization: access_token };
    await axios.put(`${basicURL}file/slides/${id}/capture-session`, payload, { headers });
  };

  const loadCapturedTilesForSession = async (
    sessionId,
    preferredCurrentTile = null,
    rowsForSession = gridRows,
    colsForSession = gridCols
  ) => {
    const access_token = store.getState().auth?.access_token;
    const headers = { Authorization: access_token };
    try {
      const res = await axios.get(
        `${basicURL}file/slides/${id}/tiles?session_id=${encodeURIComponent(sessionId)}`,
        { headers }
      );
      const mapped = {};
      (res?.data?.tiles || []).forEach((tile) => {
        const key = `${tile.row}-${tile.col}`;
        mapped[key] = {
          id: tile.id,
          url: tile.url,
          done: true,
          status: "done",
          progress: 100,
          error: null,
        };
      });
      setCapturedTiles(mapped);
      const localTotalTiles = Math.max(1, Number(rowsForSession) * Number(colsForSession));
      let firstMissing = 0;
      for (let i = 0; i < localTotalTiles; i += 1) {
        const r = Math.floor(i / colsForSession);
        const c = i % colsForSession;
        if (!mapped[`${r}-${c}`]) {
          firstMissing = i;
          break;
        }
        firstMissing = i + 1;
      }
      if (
        Number.isInteger(preferredCurrentTile) &&
        preferredCurrentTile >= 0 &&
        preferredCurrentTile < localTotalTiles
      ) {
        setCurrentTile(preferredCurrentTile);
      } else {
        setCurrentTile(Math.min(firstMissing, localTotalTiles - 1));
      }
    } catch (_) {
      setCapturedTiles({});
      const localTotalTiles = Math.max(1, Number(rowsForSession) * Number(colsForSession));
      if (
        Number.isInteger(preferredCurrentTile) &&
        preferredCurrentTile >= 0 &&
        preferredCurrentTile < localTotalTiles
      ) {
        setCurrentTile(preferredCurrentTile);
      } else {
        setCurrentTile(0);
      }
    }
  };

  const openCaptureGrid = async () => {
    try {
      const persisted = await fetchCaptureSessionConfig();
      const sessionId =
        persisted?.session_id || captureSessionId || `${id}-${Date.now()}`;
      const persistedRows = 1;
      const persistedCols = 1;
      const persistedTile = Number(persisted?.current_tile);

      const effectiveRows = persistedRows;
      const effectiveCols = persistedCols;
      setGridRows(effectiveRows);
      setGridCols(effectiveCols);
      if (typeof persisted?.ghost_enabled === "boolean") setGhostEnabled(persisted.ghost_enabled);
      if (Number.isInteger(Number(persisted?.ghost_opacity))) {
        setGhostOpacity(Math.max(0, Math.min(100, Number(persisted.ghost_opacity))));
      }
      setCaptureSessionId(sessionId);
      setCaptureGridOpen(true);
      setCaptureFeedback("");
      setScanDirection("auto");
      setNeedsRetake(false);
      setOverlapScore(null);
      setOverlapHint("");
      setReferenceStripUrl("");
      setLiveStripUrl("");
      referenceStripDataRef.current = null;

      await loadCapturedTilesForSession(
        sessionId,
        Number.isInteger(persistedTile) ? persistedTile : null,
        effectiveRows,
        effectiveCols
      );
      setIsCaptureSessionHydrated(true);
      if (!initialGuideSeen) {
        setInitialGuideSeen(true);
        setCaptureFeedback(
          "Record a slow scan video. The system will auto-detect direction and stitch frames edge-to-edge."
        );
      }
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || "Failed to load capture session.",
      });
    }
  };

  const closeCaptureGrid = () => {
    setCaptureGridOpen(false);
    stopCaptureCamera();
    setNeedsRetake(false);
    setOverlapScore(null);
    setOverlapHint("");
    setReferenceStripUrl("");
    setLiveStripUrl("");
    setScanMosaicPreviewUrl("");
    setScanAcceptedCount(0);
    setScanRejectedBlurCount(0);
    setScanRejectedMatchCount(0);
    referenceStripDataRef.current = null;
  };

  useEffect(() => {
    if (!captureGridOpen || !isCaptureSessionHydrated || !captureSessionId) return undefined;
    if (captureSessionSaveTimerRef.current) clearTimeout(captureSessionSaveTimerRef.current);
    captureSessionSaveTimerRef.current = setTimeout(() => {
      saveCaptureSessionConfig({
        session_id: captureSessionId,
        grid_rows: Number(gridRows),
        grid_cols: Number(gridCols),
        current_tile: Number(currentTile),
        ghost_enabled: !!ghostEnabled,
        ghost_opacity: Number(ghostOpacity),
      }).catch(() => {});
    }, 300);
    return () => {
      if (captureSessionSaveTimerRef.current) {
        clearTimeout(captureSessionSaveTimerRef.current);
      }
    };
  }, [
    captureGridOpen,
    isCaptureSessionHydrated,
    captureSessionId,
    gridRows,
    gridCols,
    currentTile,
    ghostEnabled,
    ghostOpacity,
  ]);

  const captureCurrentTile = async () => {
    if (!videoRef.current || !cameraReady || captureBusy) return;
    const video = videoRef.current;
    const canvas = captureCanvasRef.current || document.createElement("canvas");
    captureCanvasRef.current = canvas;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const blurScore = computeBlurScore(ctx, w, h);
    const isBlurry = blurScore < 80;

    if (isBlurry) {
      setNeedsRetake(true);
      setCaptureFeedback("Image looks blurry. Please retake this tile.");
      setToast({ open: true, severity: "warning", message: "Blurry image detected. Retake required." });
      return;
    }

    setCaptureBusy(true);
    const key = `${currentRow}-${currentCol}`;
    setCapturedTiles((prev) => ({
      ...(prev || {}),
      [key]: {
        ...(prev?.[key] || {}),
        status: "uploading",
        progress: 0,
        error: null,
      },
    }));
    try {
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.95));
      const file = new File([blob], `tile_r${currentRow}_c${currentCol}.jpg`, {
        type: "image/jpeg",
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("row", String(currentRow));
      formData.append("col", String(currentCol));
      formData.append("grid_rows", String(gridRows));
      formData.append("grid_cols", String(gridCols));
      formData.append("session_id", captureSessionId);
      formData.append("blur_score", String(Math.round(blurScore)));

      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };
      const res = await axios.post(`${basicURL}file/slides/${id}/tiles`, formData, {
        headers,
        onUploadProgress: (event) => {
          const percent = event?.total
            ? Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
            : 0;
          setCapturedTiles((prev) => ({
            ...(prev || {}),
            [key]: {
              ...(prev?.[key] || {}),
              status: "uploading",
              progress: percent,
              error: null,
            },
          }));
        },
      });
      const tileUrl = res?.data?.tile?.url;
      const tileId = res?.data?.tile?.id || null;
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          id: tileId,
          url: tileUrl,
          done: true,
          status: "done",
          progress: 100,
          error: null,
        },
      }));
      setNeedsRetake(false);
      setCaptureFeedback("Tile captured");

      if ("vibrate" in navigator) navigator.vibrate(40);
      const next = currentTile + 1;
      if (next < totalTiles) {
        setCurrentTile(next);
      } else {
        setToast({
          open: true,
          severity: "success",
          message: "All tiles captured. Ready for stitching pipeline.",
        });
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Tile upload failed.";
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          ...(prev?.[key] || {}),
          done: false,
          status: "error",
          progress: 0,
          error: errMsg,
        },
      }));
      setToast({
        open: true,
        severity: "error",
        message: errMsg,
      });
    } finally {
      setCaptureBusy(false);
    }
  };

  const uploadTileFile = async (file) => {
    if (!file || captureBusy || videoScanBusy) return;
    const key = `${currentRow}-${currentCol}`;
    setCaptureBusy(true);
    setCapturedTiles((prev) => ({
      ...(prev || {}),
      [key]: {
        ...(prev?.[key] || {}),
        status: "uploading",
        progress: 0,
        error: null,
      },
    }));
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("row", String(currentRow));
      formData.append("col", String(currentCol));
      formData.append("grid_rows", String(gridRows));
      formData.append("grid_cols", String(gridCols));
      formData.append("session_id", captureSessionId);
      formData.append("blur_score", "0");

      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };
      const res = await axios.post(`${basicURL}file/slides/${id}/tiles`, formData, {
        headers,
        onUploadProgress: (event) => {
          const percent = event?.total
            ? Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
            : 0;
          setCapturedTiles((prev) => ({
            ...(prev || {}),
            [key]: {
              ...(prev?.[key] || {}),
              status: "uploading",
              progress: percent,
              error: null,
            },
          }));
        },
      });
      const tileUrl = res?.data?.tile?.url;
      const tileId = res?.data?.tile?.id || null;
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          id: tileId,
          url: tileUrl,
          done: true,
          status: "done",
          progress: 100,
          error: null,
        },
      }));
      setNeedsRetake(false);
      setCaptureFeedback("Tile captured");
      if ("vibrate" in navigator) navigator.vibrate(40);

      const next = currentTile + 1;
      if (next < totalTiles) {
        setCurrentTile(next);
      } else {
        setToast({
          open: true,
          severity: "success",
          message: "All tiles captured. Ready for stitching pipeline.",
        });
      }
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Tile upload failed.";
      setCapturedTiles((prev) => ({
        ...(prev || {}),
        [key]: {
          ...(prev?.[key] || {}),
          done: false,
          status: "error",
          progress: 0,
          error: errMsg,
        },
      }));
      setToast({
        open: true,
        severity: "error",
        message: errMsg,
      });
    } finally {
      setCaptureBusy(false);
    }
  };

  const removeCurrentTile = async () => {
    const tile = capturedTiles?.[currentTileKey];
    if (!tile) return;
    if (captureBusy || videoScanBusy) return;
    setCaptureBusy(true);
    try {
      if (tile?.id) {
        const res = await DeleteFile(tile.id);
        if (!(res?.status === 200 || res?.status === 204)) {
          throw new Error(res?.message || "Failed to delete tile.");
        }
      }
      setCapturedTiles((prev) => {
        const next = { ...(prev || {}) };
        delete next[currentTileKey];
        return next;
      });
      setNeedsRetake(true);
      setCaptureFeedback("Tile deleted. Capture this tile again.");
      setToast({ open: true, severity: "success", message: "Tile deleted." });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.message || "Failed to delete tile.",
      });
    } finally {
      setCaptureBusy(false);
    }
  };

  const startStitchPipeline = async () => {
    if (!captureSessionId || stitchBusy || captureBusy || videoScanBusy) return;
    setStitchBusy(true);
    try {
      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };
      const res = await axios.post(
        `${basicURL}file/slides/${id}/capture-session/stitch`,
        {
          session_id: captureSessionId,
          grid_rows: Number(gridRows),
          grid_cols: Number(gridCols),
          compress_enabled: true,
          compress_quality: 95,
        },
        { headers }
      );
      const jobId = res?.data?.jobId || "";
      setStitchJobId(jobId);
      setStitchStatus("processing");
      setStitchPipelinePercent(10);
      setStitchPipelinePhase("queued");
      setStitchPipelineMessage("Queued");
      setToast({
        open: true,
        severity: "success",
        message: jobId
          ? `Stitch started (job: ${jobId.slice(0, 8)}...).`
          : "Stitch pipeline started.",
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || "Failed to start stitch pipeline.",
      });
    } finally {
      setStitchBusy(false);
    }
  };

  useEffect(() => {
    if (!stitchJobId) return undefined;
    let cancelled = false;

    const pollStatus = async () => {
      try {
        const access_token = store.getState().auth?.access_token;
        const headers = { Authorization: access_token };
        const res = await axios.get(
          `${basicURL}file/slides/${id}/capture-session/stitch/${stitchJobId}`,
          { headers }
        );
        if (cancelled) return;
        const status = String(res?.data?.jobStatus || "").toLowerCase();
        setStitchStatus(status || "processing");
        const pipeline = res?.data?.pipeline || {};
        if (typeof pipeline?.percent === "number") {
          setStitchPipelinePercent(Math.max(0, Math.min(100, pipeline.percent)));
        }
        if (pipeline?.phase) setStitchPipelinePhase(String(pipeline.phase));
        if (pipeline?.message) setStitchPipelineMessage(String(pipeline.message));
        if (status === "complete") {
          setToast({
            open: true,
            severity: "success",
            message: "Compress & tile completed. Slide reloaded.",
          });
          setStitchJobId("");
          setStitchStatus("");
          setStitchPipelinePercent(0);
          setStitchPipelinePhase("");
          setStitchPipelineMessage("");
          setCaptureGridOpen(false);
          stopCaptureCamera();
          fetchSlide();
          return;
        }
        if (status === "error") {
          setToast({
            open: true,
            severity: "error",
            message: res?.data?.error || res?.data?.message || "Compress & tile failed.",
          });
          setStitchJobId("");
          setStitchStatus("");
          setStitchPipelinePercent(0);
          setStitchPipelinePhase("");
          setStitchPipelineMessage("");
        }
      } catch (_) {
        // keep polling; transient errors are expected
      }
    };

    pollStatus();
    const timer = setInterval(pollStatus, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [stitchJobId, id]);

  const handleFallbackCaptureFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadTileFile(file);
    event.target.value = "";
  };

  const waitForVideoEvent = (video, eventName) =>
    new Promise((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("Video processing failed."));
      };
      const cleanup = () => {
        video.removeEventListener(eventName, onReady);
        video.removeEventListener("error", onError);
      };
      video.addEventListener(eventName, onReady, { once: true });
      video.addEventListener("error", onError, { once: true });
    });

  const seekVideoTo = async (video, timeSec) => {
    const safeTime = Math.max(0.01, Number(timeSec || 0));
    if (Math.abs((video.currentTime || 0) - safeTime) < 0.002) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      return;
    }
    video.currentTime = safeTime;
    await waitForVideoEvent(video, "seeked");
  };

  const extractEdgeGrayStrip = (ctx, w, h, edge) => {
    const strip = Math.max(24, Math.min(96, Math.floor(Math.min(w, h) * 0.08)));
    let sx = 0;
    let sy = 0;
    let sw = w;
    let sh = h;
    if (edge === "left") {
      sx = 0;
      sy = 0;
      sw = strip;
      sh = h;
    } else if (edge === "right") {
      sx = Math.max(0, w - strip);
      sy = 0;
      sw = strip;
      sh = h;
    } else if (edge === "top") {
      sx = 0;
      sy = 0;
      sw = w;
      sh = strip;
    } else if (edge === "bottom") {
      sx = 0;
      sy = Math.max(0, h - strip);
      sw = w;
      sh = strip;
    }
    const data = ctx.getImageData(sx, sy, sw, sh);
    return buildGrayStrip(data, sw, sh);
  };

  const buildFrameSignature = (ctx, w, h) => {
    const targetW = 64;
    const targetH = 64;
    const sample = document.createElement("canvas");
    sample.width = targetW;
    sample.height = targetH;
    const sampleCtx = sample.getContext("2d", { willReadFrequently: true });
    sampleCtx.drawImage(ctx.canvas, 0, 0, w, h, 0, 0, targetW, targetH);
    const img = sampleCtx.getImageData(0, 0, targetW, targetH);
    return buildGrayStrip(img, targetW, targetH);
  };

  const buildDownsampleGray = (ctx, w, h, side = 40) => {
    const sample = document.createElement("canvas");
    sample.width = side;
    sample.height = side;
    const sampleCtx = sample.getContext("2d", { willReadFrequently: true });
    sampleCtx.drawImage(ctx.canvas, 0, 0, w, h, 0, 0, side, side);
    const img = sampleCtx.getImageData(0, 0, side, side);
    return buildGrayStrip(img, side, side);
  };

  const estimateTranslationNcc = (prevGray, currGray, side = 96, maxShift = 10) => {
    if (
      !prevGray ||
      !currGray ||
      prevGray.length !== currGray.length ||
      prevGray.length !== side * side
    ) {
      return null;
    }
    let best = null;
    for (let dy = -maxShift; dy <= maxShift; dy += 1) {
      for (let dx = -maxShift; dx <= maxShift; dx += 1) {
        if (dx === 0 && dy === 0) continue;
        let sumA = 0;
        let sumB = 0;
        let sumAA = 0;
        let sumBB = 0;
        let sumAB = 0;
        let n = 0;
        for (let y = 0; y < side; y += 1) {
          const y2 = y + dy;
          if (y2 < 0 || y2 >= side) continue;
          for (let x = 0; x < side; x += 1) {
            const x2 = x + dx;
            if (x2 < 0 || x2 >= side) continue;
            const i1 = y * side + x;
            const i2 = y2 * side + x2;
            const a = prevGray[i1];
            const b = currGray[i2];
            sumA += a;
            sumB += b;
            sumAA += a * a;
            sumBB += b * b;
            sumAB += a * b;
            n += 1;
          }
        }
        if (n < side * side * 0.45) continue;
        const num = n * sumAB - sumA * sumB;
        const denA = n * sumAA - sumA * sumA;
        const denB = n * sumBB - sumB * sumB;
        const den = Math.sqrt(Math.max(1e-9, denA * denB));
        const ncc = den > 0 ? num / den : -1;
        if (!best || ncc > best.ncc) {
          best = { dx, dy, ncc };
        }
      }
    }
    if (!best) return null;
    return {
      dx: best.dx,
      dy: best.dy,
      quality: Math.round(Math.max(0, Math.min(100, ((best.ncc + 1) / 2) * 100))),
      ncc: best.ncc,
    };
  };

  const buildFixedEdgeSignature = (ctx, w, h, edge, major = 96, minor = 20) => {
    const c = document.createElement("canvas");
    if (edge === "left" || edge === "right") {
      c.width = minor;
      c.height = major;
    } else {
      c.width = major;
      c.height = minor;
    }
    const sctx = c.getContext("2d", { willReadFrequently: true });
    const stripW = Math.max(10, Math.floor(w * 0.12));
    const stripH = Math.max(10, Math.floor(h * 0.12));
    if (edge === "left") {
      sctx.drawImage(ctx.canvas, 0, 0, stripW, h, 0, 0, c.width, c.height);
    } else if (edge === "right") {
      sctx.drawImage(ctx.canvas, Math.max(0, w - stripW), 0, stripW, h, 0, 0, c.width, c.height);
    } else if (edge === "top") {
      sctx.drawImage(ctx.canvas, 0, 0, w, stripH, 0, 0, c.width, c.height);
    } else {
      sctx.drawImage(ctx.canvas, 0, Math.max(0, h - stripH), w, stripH, 0, 0, c.width, c.height);
    }
    const img = sctx.getImageData(0, 0, c.width, c.height);
    return buildGrayStrip(img, c.width, c.height);
  };

  const estimateOrthogonalShiftFromStrips = (
    prevStrip,
    currStrip,
    major = 96,
    minor = 20,
    maxShift = 10
  ) => {
    if (
      !prevStrip ||
      !currStrip ||
      prevStrip.length !== currStrip.length ||
      prevStrip.length !== major * minor
    ) {
      return null;
    }
    let best = null;
    for (let shift = -maxShift; shift <= maxShift; shift += 1) {
      let diff = 0;
      let count = 0;
      for (let y = 0; y < major; y += 1) {
        const y2 = y + shift;
        if (y2 < 0 || y2 >= major) continue;
        for (let x = 0; x < minor; x += 1) {
          const i1 = y * minor + x;
          const i2 = y2 * minor + x;
          diff += Math.abs(prevStrip[i1] - currStrip[i2]);
          count += 1;
        }
      }
      if (count < major * minor * 0.45) continue;
      const score = 100 - (diff / count / 255) * 100;
      if (!best || score > best.score) best = { shift, score };
    }
    return best;
  };

  const smoothSeries = (values, radius = 2) => {
    return values.map((_, i) => {
      const start = Math.max(0, i - radius);
      const end = Math.min(values.length - 1, i + radius);
      let sum = 0;
      let n = 0;
      for (let j = start; j <= end; j += 1) {
        sum += values[j];
        n += 1;
      }
      return n ? sum / n : values[i];
    });
  };

  const medianValue = (values) => {
    if (!Array.isArray(values) || values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 0) return (sorted[mid - 1] + sorted[mid]) / 2;
    return sorted[mid];
  };

  const refineAcceptedFrames = (frames, axis) => {
    if (!Array.isArray(frames) || frames.length < 2) return frames || [];
    const out = [{ ...frames[0], x: 0, y: 0, dx: 0, dy: 0 }];
    const mainDeltas = frames
      .slice(1)
      .map((f) => (axis === "vertical" ? Number(f.dy || 0) : Number(f.dx || 0)))
      .filter((v) => Math.abs(v) > 0.01);
    const signScore = mainDeltas.reduce((acc, v) => acc + (v >= 0 ? 1 : -1), 0);
    const mainSign = signScore >= 0 ? 1 : -1;

    for (let i = 1; i < frames.length; i += 1) {
      const prev = out[out.length - 1];
      const currRaw = frames[i];
      let dx = Number(currRaw.dx || 0);
      let dy = Number(currRaw.dy || 0);
      const fw = Number(currRaw.frameW || prev.frameW || 0);
      const fh = Number(currRaw.frameH || prev.frameH || 0);

      if (axis === "horizontal") {
        const main = dx;
        const minMain = Math.max(2, fw * 0.01);
        const maxMain = Math.max(26, fw * 0.35);
        if (Math.abs(main) < minMain) continue;
        if (Math.sign(main) !== mainSign) continue;
        dx = Math.max(minMain, Math.min(maxMain, Math.abs(main))) * mainSign;

        const stripShift = estimateOrthogonalShiftFromStrips(
          prev.rightStrip96,
          currRaw.leftStrip96,
          96,
          20,
          5
        );
        const seamDy = stripShift ? stripShift.shift * (fh / 96) : dy;
        const seamWeight = stripShift?.score > 58 ? 0.25 : 0;
        const orth = seamWeight * seamDy + (1 - seamWeight) * dy;
        const maxOrth = Math.max(6, fh * 0.04);
        dy = Math.max(-maxOrth, Math.min(maxOrth, orth));
      } else {
        const main = dy;
        const minMain = Math.max(2, fh * 0.01);
        const maxMain = Math.max(26, fh * 0.35);
        if (Math.abs(main) < minMain) continue;
        if (Math.sign(main) !== mainSign) continue;
        dy = Math.max(minMain, Math.min(maxMain, Math.abs(main))) * mainSign;

        const stripShift = estimateOrthogonalShiftFromStrips(
          prev.bottomStrip96,
          currRaw.topStrip96,
          96,
          20,
          5
        );
        const seamDx = stripShift ? stripShift.shift * (fw / 96) : dx;
        const seamWeight = stripShift?.score > 58 ? 0.25 : 0;
        const orth = seamWeight * seamDx + (1 - seamWeight) * dx;
        const maxOrth = Math.max(6, fw * 0.04);
        dx = Math.max(-maxOrth, Math.min(maxOrth, orth));
      }

      out.push({
        ...currRaw,
        dx,
        dy,
        x: prev.x + dx,
        y: prev.y + dy,
      });
    }

    if (out.length < 2) return [frames[0]];

    if (axis === "horizontal") {
      const smoothedY = smoothSeries(out.map((f) => Number(f.y || 0)), 2);
      const slope = (smoothedY[smoothedY.length - 1] - smoothedY[0]) / Math.max(1, smoothedY.length - 1);
      const detrended = smoothedY.map((v, i) => v - slope * i);
      const centered = detrended.map((v) => v - medianValue(detrended));
      const clampY = Math.max(5, Number(out[0]?.frameH || 0) * 0.03);
      for (let i = 1; i < out.length; i += 1) {
        out[i].y = Math.max(-clampY, Math.min(clampY, centered[i]));
        out[i].dy = out[i].y - out[i - 1].y;
      }
    } else {
      const smoothedX = smoothSeries(out.map((f) => Number(f.x || 0)), 2);
      const slope = (smoothedX[smoothedX.length - 1] - smoothedX[0]) / Math.max(1, smoothedX.length - 1);
      const detrended = smoothedX.map((v, i) => v - slope * i);
      const centered = detrended.map((v) => v - medianValue(detrended));
      const clampX = Math.max(5, Number(out[0]?.frameW || 0) * 0.03);
      for (let i = 1; i < out.length; i += 1) {
        out[i].x = Math.max(-clampX, Math.min(clampX, centered[i]));
        out[i].dx = out[i].x - out[i - 1].x;
      }
    }

    return out;
  };

  const buildMosaicPreviewDataUrl = async (frames, frameW, frameH, axis = "horizontal") => {
    if (!frames?.length) return "";
    const minX = Math.min(...frames.map((f) => f.x));
    const minY = Math.min(...frames.map((f) => f.y));
    const maxX = Math.max(...frames.map((f) => f.x));
    const maxY = Math.max(...frames.map((f) => f.y));
    const spanW = Math.max(frameW, maxX - minX + frameW);
    const spanH = Math.max(frameH, maxY - minY + frameH);
    const maxTarget = 1200;
    const scale = Math.min(1, maxTarget / Math.max(spanW, spanH));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(spanW * scale));
    canvas.height = Math.max(1, Math.round(spanH * scale));
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const minPatch = Math.max(6, Math.round(Math.min(frameW, frameH) * 0.015));
    const maxPatchW = Math.max(minPatch + 2, Math.round(frameW * 0.42));
    const maxPatchH = Math.max(minPatch + 2, Math.round(frameH * 0.42));

    for (let i = 0; i < frames.length; i += 1) {
      const frame = frames[i];
      const bitmap = await createImageBitmap(frame.file);
      const drawFrameX = frame.x - minX;
      const drawFrameY = frame.y - minY;
      if (i === 0) {
        ctx.globalAlpha = 1;
        ctx.drawImage(
          bitmap,
          0,
          0,
          frameW,
          frameH,
          Math.round(drawFrameX * scale),
          Math.round(drawFrameY * scale),
          Math.round(frameW * scale),
          Math.round(frameH * scale)
        );
        bitmap.close();
        continue;
      }

      // Draw only newly discovered strip from each frame (small puzzle-like contribution).
      const dx = Number(frame.dx || 0);
      const dy = Number(frame.dy || 0);
      if (axis === "vertical") {
        const patchH = Math.max(minPatch, Math.min(maxPatchH, Math.round(Math.abs(dy) || minPatch)));
        const sy = dy >= 0 ? Math.max(0, frameH - patchH) : 0;
        const dstX = drawFrameX;
        const dstY = drawFrameY + sy;
        ctx.globalAlpha = 1;
        ctx.drawImage(
          bitmap,
          0,
          sy,
          frameW,
          patchH,
          Math.round(dstX * scale),
          Math.round(dstY * scale),
          Math.round(frameW * scale),
          Math.max(1, Math.round(patchH * scale))
        );
      } else {
        const patchW = Math.max(minPatch, Math.min(maxPatchW, Math.round(Math.abs(dx) || minPatch)));
        const sx = dx >= 0 ? Math.max(0, frameW - patchW) : 0;
        const dstX = drawFrameX + sx;
        const dstY = drawFrameY;
        ctx.globalAlpha = 1;
        ctx.drawImage(
          bitmap,
          sx,
          0,
          patchW,
          frameH,
          Math.round(dstX * scale),
          Math.round(dstY * scale),
          Math.max(1, Math.round(patchW * scale)),
          Math.round(frameH * scale)
        );
      }
      bitmap.close();
    }
    ctx.globalAlpha = 1;
    return canvas.toDataURL("image/jpeg", 0.88);
  };

  const estimateBestRelativeShift = (prevGray, currGray, side = 40, axisHint = "auto") => {
    if (!prevGray || !currGray || prevGray.length !== currGray.length || prevGray.length !== side * side) {
      return null;
    }
    const rangeMajor = 8;
    const rangeMinor = 3;
    const dxStart = axisHint === "vertical" ? -rangeMinor : -rangeMajor;
    const dxEnd = axisHint === "vertical" ? rangeMinor : rangeMajor;
    const dyStart = axisHint === "horizontal" ? -rangeMinor : -rangeMajor;
    const dyEnd = axisHint === "horizontal" ? rangeMinor : rangeMajor;

    let best = null;
    for (let dy = dyStart; dy <= dyEnd; dy += 1) {
      for (let dx = dxStart; dx <= dxEnd; dx += 1) {
        if (dx === 0 && dy === 0) continue;
        let diff = 0;
        let count = 0;
        for (let y = 0; y < side; y += 1) {
          const y2 = y + dy;
          if (y2 < 0 || y2 >= side) continue;
          for (let x = 0; x < side; x += 1) {
            const x2 = x + dx;
            if (x2 < 0 || x2 >= side) continue;
            const i1 = y * side + x;
            const i2 = y2 * side + x2;
            diff += Math.abs(prevGray[i1] - currGray[i2]);
            count += 1;
          }
        }
        if (!count || count < side * side * 0.45) continue;
        const meanDiff = diff / count;
        const quality = Math.max(0, Math.min(100, 100 - (meanDiff / 255) * 100));
        const move = Math.sqrt(dx * dx + dy * dy);
        if (!best || quality > best.quality) {
          best = { dx, dy, quality, move };
        }
      }
    }
    return best;
  };

  const frameDifferenceScore = (a, b) => {
    if (!a || !b || a.length !== b.length || !a.length) return 0;
    let diff = 0;
    for (let i = 0; i < a.length; i += 1) {
      diff += Math.abs(a[i] - b[i]);
    }
    const mean = diff / a.length;
    return Math.max(0, Math.min(100, Math.round((mean / 255) * 100)));
  };

  const canvasToBlob = (srcCanvas, type = "image/jpeg", quality = 0.93) =>
    new Promise((resolve) => srcCanvas.toBlob(resolve, type, quality));

  const estimateAxisShiftFromSignatures = (prevSig, currSig, axis = "horizontal") => {
    if (!prevSig || !currSig || prevSig.length !== currSig.length || !prevSig.length) {
      return { px: 0, quality: 0 };
    }
    const side = Math.round(Math.sqrt(prevSig.length));
    if (!side || side * side !== prevSig.length) return { px: 0, quality: 0 };

    const maxShift = Math.max(2, Math.floor(side * 0.35));
    let best = { px: 0, quality: -Infinity };

    const scoreShift = (shift) => {
      let diff = 0;
      let count = 0;
      if (axis === "horizontal") {
        for (let y = 0; y < side; y += 1) {
          for (let x = 0; x < side; x += 1) {
            const x2 = x + shift;
            if (x2 < 0 || x2 >= side) continue;
            const i1 = y * side + x;
            const i2 = y * side + x2;
            diff += Math.abs(prevSig[i1] - currSig[i2]);
            count += 1;
          }
        }
      } else {
        for (let y = 0; y < side; y += 1) {
          const y2 = y + shift;
          if (y2 < 0 || y2 >= side) continue;
          for (let x = 0; x < side; x += 1) {
            const i1 = y * side + x;
            const i2 = y2 * side + x;
            diff += Math.abs(prevSig[i1] - currSig[i2]);
            count += 1;
          }
        }
      }
      if (!count) return -Infinity;
      const meanDiff = diff / count;
      return 100 - (meanDiff / 255) * 100;
    };

    for (let s = -maxShift; s <= maxShift; s += 1) {
      if (s === 0) continue;
      const quality = scoreShift(s);
      if (quality > best.quality) {
        best = { px: s, quality };
      }
    }
    return best;
  };

  const buildNonOverlapPreviewBlob = async (frameCanvas, axis, shiftPx, isFirstFrame) => {
    if (isFirstFrame || !shiftPx) {
      return canvasToBlob(frameCanvas, "image/jpeg", 0.9);
    }
    const w = frameCanvas.width;
    const h = frameCanvas.height;
    const normalized = Math.max(0.08, Math.min(0.35, Math.abs(shiftPx) / 64));
    const previewCanvas = document.createElement("canvas");
    const previewCtx = previewCanvas.getContext("2d");
    if (axis === "horizontal") {
      const stripW = Math.max(18, Math.min(w, Math.round(w * normalized)));
      const sx = shiftPx < 0 ? Math.max(0, w - stripW) : 0;
      previewCanvas.width = stripW;
      previewCanvas.height = h;
      previewCtx.drawImage(frameCanvas, sx, 0, stripW, h, 0, 0, stripW, h);
    } else {
      const stripH = Math.max(18, Math.min(h, Math.round(h * normalized)));
      const sy = shiftPx < 0 ? Math.max(0, h - stripH) : 0;
      previewCanvas.width = w;
      previewCanvas.height = stripH;
      previewCtx.drawImage(frameCanvas, 0, sy, w, stripH, 0, 0, w, stripH);
    }
    return canvasToBlob(previewCanvas, "image/jpeg", 0.88);
  };

  const getScanRoiRect = (w, h) => {
    const roiW = Math.max(120, Math.floor(w * 0.52));
    const roiH = Math.max(120, Math.floor(h * 0.8));
    const sx = Math.max(0, Math.floor((w - roiW) / 2));
    const sy = Math.max(0, Math.floor((h - roiH) / 2));
    return { sx, sy, sw: Math.min(roiW, w), sh: Math.min(roiH, h) };
  };

  const drawVideoFrameToRoiCanvas = (video, targetCanvas) => {
    const fullW = video.videoWidth || 1280;
    const fullH = video.videoHeight || 720;
    const roi = getScanRoiRect(fullW, fullH);
    targetCanvas.width = roi.sw;
    targetCanvas.height = roi.sh;
    const ctx = targetCanvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(
      video,
      roi.sx,
      roi.sy,
      roi.sw,
      roi.sh,
      0,
      0,
      roi.sw,
      roi.sh
    );
    return { ctx, w: roi.sw, h: roi.sh };
  };

  const revokePreparedBlobUrls = (tilesMap = {}) => {
    Object.values(tilesMap || {}).forEach((tile) => {
      const url = String(tile?.url || "");
      if (url.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {
          // ignore
        }
      }
    });
  };

  const handleVideoScanFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (captureBusy || videoScanBusy || uploadPreparedBusy) {
      event.target.value = "";
      return;
    }

    setVideoScanBusy(true);
    setVideoScanProgress(0);
    setVideoScanMessage("Preparing video scan...");
    setCaptureFeedback("Processing video scan...");

    const maxPreparedFrames = 90;
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;

    const canvas = captureCanvasRef.current || document.createElement("canvas");
    captureCanvasRef.current = canvas;

    try {
      await waitForVideoEvent(video, "loadedmetadata");
      const duration = Number(video.duration || 0);
      if (!duration || Number.isNaN(duration)) {
        throw new Error("Video duration is invalid. Please upload another video.");
      }

      const minBlur = 18;
      const sampleCount = Math.max(90, Math.min(420, Math.round(duration * 16)));
      const acceptedFrames = [];
      let rejectedBlur = 0;
      let rejectedMatch = 0;
      let dominantAxis = "auto";

      revokePreparedBlobUrls(capturedTiles);
      preparedTileBlobRef.current = {};
      setScanMosaicPreviewUrl("");
      setScanAcceptedCount(0);
      setScanRejectedBlurCount(0);
      setScanRejectedMatchCount(0);

      for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
        const ratio = sampleCount <= 1 ? 0 : sampleIndex / (sampleCount - 1);
        const t = Math.min(Math.max(0.05, duration * ratio), Math.max(0.05, duration - 0.05));
        setVideoScanMessage(
          `Scanning video ${sampleIndex + 1}/${sampleCount} and matching relative pattern...`
        );
        await seekVideoTo(video, t);

        const { ctx, w, h } = drawVideoFrameToRoiCanvas(video, canvas);
        const blurScore = computeBlurScore(ctx, w, h);
        if (blurScore < minBlur) {
          rejectedBlur += 1;
          setScanRejectedBlurCount(rejectedBlur);
          setVideoScanProgress(Math.round(((sampleIndex + 1) / sampleCount) * 45));
          continue;
        }

        const gray96 = buildDownsampleGray(ctx, w, h, 96);
        const blob = await canvasToBlob(canvas, "image/jpeg", 0.93);
        if (!blob) continue;

        if (!acceptedFrames.length) {
          acceptedFrames.push({
            t,
            x: 0,
            y: 0,
            dx: 0,
            dy: 0,
            quality: 100,
            blurScore,
            gray96,
            leftStrip96: buildFixedEdgeSignature(ctx, w, h, "left", 96, 20),
            rightStrip96: buildFixedEdgeSignature(ctx, w, h, "right", 96, 20),
            topStrip96: buildFixedEdgeSignature(ctx, w, h, "top", 96, 20),
            bottomStrip96: buildFixedEdgeSignature(ctx, w, h, "bottom", 96, 20),
            file: blob,
            frameW: w,
            frameH: h,
          });
          setScanAcceptedCount(1);
          setVideoScanProgress(Math.round(((sampleIndex + 1) / sampleCount) * 45));
          continue;
        }

        const prev = acceptedFrames[acceptedFrames.length - 1];
        const rel = estimateTranslationNcc(prev.gray96, gray96, 96, 11);
        if (!rel || rel.quality < 52) {
          rejectedMatch += 1;
          setScanRejectedMatchCount(rejectedMatch);
          setVideoScanProgress(Math.round(((sampleIndex + 1) / sampleCount) * 45));
          continue;
        }

        const scaleX = prev.frameW / 96;
        const scaleY = prev.frameH / 96;
        const dxPx = rel.dx * scaleX;
        const dyPx = rel.dy * scaleY;
        const move = Math.hypot(dxPx, dyPx);
        const moveLower = Math.max(2, Math.min(prev.frameW, prev.frameH) * 0.012);
        const moveUpper = Math.max(26, Math.min(prev.frameW, prev.frameH) * 0.38);
        if (move < moveLower || move > moveUpper) {
          rejectedMatch += 1;
          setScanRejectedMatchCount(rejectedMatch);
          setVideoScanProgress(Math.round(((sampleIndex + 1) / sampleCount) * 45));
          continue;
        }

        if (dominantAxis === "auto" && acceptedFrames.length >= 3) {
          const sx = acceptedFrames.reduce((acc, f) => acc + Math.abs(f.dx || 0), 0) + Math.abs(dxPx);
          const sy = acceptedFrames.reduce((acc, f) => acc + Math.abs(f.dy || 0), 0) + Math.abs(dyPx);
          dominantAxis = sy > sx ? "vertical" : "horizontal";
        }
        if (dominantAxis === "horizontal" && Math.abs(dxPx) < Math.abs(dyPx) * 1.15) {
          rejectedMatch += 1;
          setScanRejectedMatchCount(rejectedMatch);
          setVideoScanProgress(Math.round(((sampleIndex + 1) / sampleCount) * 45));
          continue;
        }
        if (dominantAxis === "vertical" && Math.abs(dyPx) < Math.abs(dxPx) * 1.15) {
          rejectedMatch += 1;
          setScanRejectedMatchCount(rejectedMatch);
          setVideoScanProgress(Math.round(((sampleIndex + 1) / sampleCount) * 45));
          continue;
        }

        acceptedFrames.push({
          t,
          x: prev.x + dxPx,
          y: prev.y + dyPx,
          dx: dxPx,
          dy: dyPx,
          quality: rel.quality,
          blurScore,
          gray96,
          leftStrip96: buildFixedEdgeSignature(ctx, w, h, "left", 96, 20),
          rightStrip96: buildFixedEdgeSignature(ctx, w, h, "right", 96, 20),
          topStrip96: buildFixedEdgeSignature(ctx, w, h, "top", 96, 20),
          bottomStrip96: buildFixedEdgeSignature(ctx, w, h, "bottom", 96, 20),
          file: blob,
          frameW: w,
          frameH: h,
        });
        setScanAcceptedCount(acceptedFrames.length);
        if (acceptedFrames.length >= maxPreparedFrames) break;
        setVideoScanProgress(Math.round(((sampleIndex + 1) / sampleCount) * 45));
      }

      if (acceptedFrames.length < 4) {
        throw new Error(
          "Not enough linked frames found. Please scan slower with smoother movement."
        );
      }

      const axis = dominantAxis === "auto" ? "horizontal" : dominantAxis;
      const isVertical = axis === "vertical";
      setScanDirection(axis);
      const refinedFrames = refineAcceptedFrames(acceptedFrames, axis);
      const preparedFrameCount = refinedFrames.length;
      if (preparedFrameCount < 4) {
        throw new Error("Linked frames became unstable after edge refinement. Please rescan slower.");
      }
      setGridRows(isVertical ? preparedFrameCount : 1);
      setGridCols(isVertical ? 1 : preparedFrameCount);
      setVideoScanProgress(60);

      const preparedNext = {};
      for (let i = 0; i < preparedFrameCount; i += 1) {
        const row = isVertical ? i : 0;
        const col = isVertical ? 0 : i;
        const key = `${row}-${col}`;
        const frame = refinedFrames[i];
        const file = new File([frame.file], `tile_r${row}_c${col}.jpg`, {
          type: "image/jpeg",
        });
        const previewUrl = URL.createObjectURL(frame.file);
        preparedTileBlobRef.current[key] = {
          file,
          blurScore: Math.round(frame.blurScore || 0),
          row,
          col,
        };
        preparedNext[key] = {
          id: null,
          url: previewUrl,
          display_url: previewUrl,
          done: false,
          status: "prepared",
          progress: 0,
          error: null,
        };
      }
      setCapturedTiles(preparedNext);

      setVideoScanMessage("Building stitched preview from linked frames...");
      const previewUrl = await buildMosaicPreviewDataUrl(
        refinedFrames,
        refinedFrames[0].frameW,
        refinedFrames[0].frameH,
        axis
      );
      setScanMosaicPreviewUrl(previewUrl);
      setVideoScanProgress(100);

      setCurrentTile(0);

      setNeedsRetake(false);
      setVideoScanMessage("Video analysis completed. Frames linked by relative pattern (not uploaded).");
      setCaptureFeedback("Grid prepared with small stitched parts. Click 'Upload Prepared Grid' to start uploading.");
      setToast({
        open: true,
        severity: "success",
        message: "Relative-pattern frames prepared locally.",
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || error?.message || "Video scan failed.",
      });
      setVideoScanMessage("Video scan failed.");
    } finally {
      URL.revokeObjectURL(objectUrl);
      setVideoScanBusy(false);
      if (event?.target) event.target.value = "";
    }
  };

  const uploadPreparedTiles = async () => {
    if (videoScanBusy || stitchBusy || uploadPreparedBusy) return;
    const entries = Object.entries(preparedTileBlobRef.current || {}).sort((a, b) => {
      const [ar, ac] = a[0].split("-").map(Number);
      const [br, bc] = b[0].split("-").map(Number);
      if (ar !== br) return ar - br;
      return ac - bc;
    });
    if (!entries.length) {
      setToast({
        open: true,
        severity: "info",
        message: "No prepared frames to upload.",
      });
      return;
    }

    setUploadPreparedBusy(true);
    setUploadPreparedProgress(0);
    setCaptureFeedback("Uploading prepared grid...");
    try {
      const access_token = store.getState().auth?.access_token;
      const headers = { Authorization: access_token };

      for (let idx = 0; idx < entries.length; idx += 1) {
        const [key, meta] = entries[idx];
        const row = Number(meta.row);
        const col = Number(meta.col);
        const file = meta.file;

        setCapturedTiles((prev) => ({
          ...(prev || {}),
          [key]: {
            ...(prev?.[key] || {}),
            status: "uploading",
            progress: 0,
            error: null,
          },
        }));

        const formData = new FormData();
        formData.append("file", file);
        formData.append("row", String(row));
        formData.append("col", String(col));
        formData.append("grid_rows", String(gridRows));
        formData.append("grid_cols", String(gridCols));
        formData.append("session_id", captureSessionId);
        formData.append("blur_score", String(Math.round(meta.blurScore || 0)));

        const res = await axios.post(`${basicURL}file/slides/${id}/tiles`, formData, {
          headers,
          onUploadProgress: (uploadEvent) => {
            const percent = uploadEvent?.total
              ? Math.max(0, Math.min(100, Math.round((uploadEvent.loaded / uploadEvent.total) * 100)))
              : 0;
            setCapturedTiles((prev) => ({
              ...(prev || {}),
              [key]: {
                ...(prev?.[key] || {}),
                status: "uploading",
                progress: percent,
                error: null,
              },
            }));
          },
        });

        setCapturedTiles((prev) => {
          const prevUrl = prev?.[key]?.url;
          if (String(prevUrl || "").startsWith("blob:")) {
            try {
              URL.revokeObjectURL(prevUrl);
            } catch (_) {
              // ignore
            }
          }
          return {
            ...(prev || {}),
            [key]: {
              id: res?.data?.tile?.id || null,
              url: res?.data?.tile?.url || "",
              done: true,
              status: "done",
              progress: 100,
              error: null,
            },
          };
        });
        delete preparedTileBlobRef.current[key];
        setUploadPreparedProgress(Math.round(((idx + 1) / entries.length) * 100));
      }

      setCaptureFeedback("Prepared grid uploaded. Ready for stitching pipeline.");
      setToast({
        open: true,
        severity: "success",
        message: "Prepared grid uploaded successfully.",
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || error?.message || "Prepared upload failed.",
      });
    } finally {
      setUploadPreparedBusy(false);
    }
  };

  const deleteUploadedTiles = async () => {
    const entries = Object.entries(capturedTiles || {});
    if (!entries.length || deleteUploadedBusy || videoScanBusy || stitchBusy || uploadPreparedBusy)
      return;

    const confirmation = await openAppConfirm({
      title: "Delete uploaded tiles?",
      message:
        "This will remove all uploaded tiles from this scan session. You can record a new scan after deletion.",
      confirmText: "Delete",
      cancelText: "Cancel",
      confirmColor: "error",
    });
    if (!confirmation) return;

    setDeleteUploadedBusy(true);
    setCaptureFeedback("Deleting uploaded tiles...");
    try {
      revokePreparedBlobUrls(capturedTiles);
      preparedTileBlobRef.current = {};
      const nextTiles = {};
      let failed = 0;

      for (const [key, tile] of entries) {
        if (!tile?.id) continue;
        try {
          const res = await DeleteFile(tile.id);
          if (res?.status === 200 || res?.status === 204) {
            // uploaded tile deleted from backend
          } else {
            failed += 1;
            nextTiles[key] = {
              ...(nextTiles[key] || {}),
              status: "error",
              error: res?.message || "Failed to delete tile.",
            };
          }
        } catch (error) {
          failed += 1;
          nextTiles[key] = {
            ...(nextTiles[key] || {}),
            status: "error",
            error: error?.message || "Failed to delete tile.",
          };
        }
      }

      setCapturedTiles(nextTiles);
      if (Object.keys(nextTiles).length === 0) {
        setCurrentTile(0);
        setGridRows(1);
        setGridCols(1);
        setScanDirection("auto");
        setScanMosaicPreviewUrl("");
        setScanAcceptedCount(0);
        setScanRejectedBlurCount(0);
        setScanRejectedMatchCount(0);
      }

      if (failed === 0) {
        setCaptureFeedback("All uploaded tiles deleted.");
        setToast({
          open: true,
          severity: "success",
          message: "Uploaded tiles deleted successfully.",
        });
      } else {
        setCaptureFeedback("Some tiles could not be deleted.");
        setToast({
          open: true,
          severity: "warning",
          message: `${failed} tile(s) failed to delete. Please retry.`,
        });
      }
    } finally {
      setDeleteUploadedBusy(false);
    }
  };

  useEffect(() => {
    if (!captureGridOpen) return;
    const ref = getReferenceInfoForCurrentTile();
    setReferenceTile(ref.tile);
    setReferenceDirection(ref.direction);
    setOverlapScore(null);
    setOverlapHint("");
  }, [captureGridOpen, currentTile, capturedTiles, gridCols]);

  useEffect(() => {
    let cancelled = false;
    const buildReferenceStrip = async () => {
      if (!referenceTile?.url || !referenceDirection) {
        setReferenceStripUrl("");
        referenceStripDataRef.current = null;
        return;
      }
      try {
        const { stripUrl, stripGray } = await extractStripFromImage(
          referenceTile.url,
          referenceDirection
        );
        if (cancelled) return;
        setReferenceStripUrl(stripUrl);
        referenceStripDataRef.current = stripGray;
      } catch (_) {
        if (cancelled) return;
        setReferenceStripUrl("");
        referenceStripDataRef.current = null;
      }
    };
    buildReferenceStrip();
    return () => {
      cancelled = true;
    };
  }, [referenceTile, referenceDirection]);

  useEffect(() => {
    if (!captureGridOpen || !cameraReady || !referenceDirection || !referenceStripDataRef.current) {
      return;
    }
    if (!window.isSecureContext || !videoRef.current) return;

    const interval = setInterval(() => {
      try {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) return;
        const canvas = liveStripCanvasRef.current || document.createElement("canvas");
        liveStripCanvasRef.current = canvas;
        const w = video.videoWidth;
        const h = video.videoHeight;
        const strip = 80;
        if (referenceDirection === "horizontal") {
          canvas.width = strip;
          canvas.height = h;
        } else {
          canvas.width = w;
          canvas.height = strip;
        }
        const ctx = canvas.getContext("2d");
        if (referenceDirection === "horizontal") {
          // Current tile should align its left edge with reference tile right edge.
          ctx.drawImage(video, 0, 0, strip, h, 0, 0, strip, h);
        } else {
          // Current tile should align its top edge with reference tile bottom edge.
          ctx.drawImage(video, 0, 0, w, strip, 0, 0, w, strip);
        }
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const liveGray = buildGrayStrip(imgData, canvas.width, canvas.height);
        const score = compareGrayStrips(referenceStripDataRef.current, liveGray);
        setOverlapScore(score);
        const cls = classifyOverlap(score);
        setOverlapHint(cls.label);
        setLiveStripUrl(canvas.toDataURL("image/jpeg", 0.8));
      } catch (_) {
        // ignore transient frame errors
      }
    }, 700);

    return () => clearInterval(interval);
  }, [captureGridOpen, cameraReady, referenceDirection, referenceStripUrl]);

  useEffect(() => () => stopCaptureCamera(), []);

  const privateView =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("private") === "1";
  const roleKey = String(role || "").toLowerCase();
  const canManageStudentPinVisibility =
    roleKey === "prof" ||
    roleKey === "professor" ||
    roleKey === "university_admin" ||
    roleKey === "admin";
  const showStudentPinToggle =
    privateView &&
    canManageStudentPinVisibility &&
    isCorePublicSlide &&
    !isEmpty(geoJson) &&
    !!geoJson?.id;

  const hidePathologyPanel =
    isUniversityAdmin || roleKey === "university" || impersonatingUniversity;
  const showPathologyFeature =
    resolvedCoreType === "his" && !isEmpty(geoJson) && !!tileURL && !hidePathologyPanel;
  const showPathologyAdmin =
    showPathologyFeature &&
    (isPlatformAdmin || canForSlideType("his", "ai_pathology"));

  useEffect(() => {
    if (!showStudentPinToggle) return;
    let cancelled = false;
    (async () => {
      const vis = await GetLibraryVisibilityCall();
      if (cancelled || !vis) return;
      setStudentsSeePublicPins(vis.students_see_public_library_pins === true);
    })();
    return () => {
      cancelled = true;
    };
  }, [showStudentPinToggle, geoJson?.id]);

  const handleStudentsPublicPinsChange = async (_e, checked) => {
    setStudentsSeePublicPins(checked);
    await UpdateLibraryVisibilityCall({ students_see_public_library_pins: checked });
    fetchSlide();
  };

  const dialogStackProps = {
    isMobile,
    slideSubject:
      resolvedCoreType === "emb"
        ? "embryology"
        : resolvedCoreType === "rad"
          ? "radiology"
        : resolvedCoreType === "his"
          ? "histology"
          : "histology",
    aiOpen,
    onAiClose: () => setAiOpen(false),
    aiError,
    captureLoading,
    capturedImage,
    aiMessages,
    aiQuestion,
    setAiQuestion,
    onSend: sendAiQuestion,
    aiLoading,
    captureLoadingForDisable: captureLoading,
    chatMessagesRef,
    hasDrawing,
    onClearDrawing: clearDrawing,
    getContextTitle: getSlideContextTitle,
    setupDrawingCanvas,
    baseImageRef,
    drawCanvasRef,
    onDrawStart,
    onDrawMove,
    onDrawEnd,
    aiCaptureViewportRef: undefined,
    captureGridOpen,
    onCaptureClose: closeCaptureGrid,
    videoRef,
    captureViewportRef,
    fallbackCameraInputRef,
    handleFallbackCaptureFile,
    videoScanInputRef,
    handleVideoScanFile,
    videoScanBusy,
    videoScanProgress,
    videoScanMessage,
    uploadPreparedBusy,
    uploadPreparedProgress,
    onUploadPreparedTiles: uploadPreparedTiles,
    canUploadPreparedTiles: preparedCount > 0,
    onDeleteUploadedTiles: deleteUploadedTiles,
    deleteUploadedBusy,
    canDeleteUploadedTiles:
      Object.values(capturedTiles || {}).some((t) => t?.done && t?.id) || preparedCount > 0,
    preparedCount,
    scanDirection,
    scanMosaicPreviewUrl,
    scanAcceptedCount,
    scanRejectedBlurCount,
    scanRejectedMatchCount,
    gridRows,
    gridCols,
    setGridRows,
    setGridCols,
    totalTiles,
    capturedCount,
    capturePercent,
    currentTile,
    currentTileKey,
    currentRow,
    currentCol,
    capturedTiles,
    onSelectTile: setCurrentTile,
    captureFeedback,
    needsRetake,
    captureBusy,
    cameraReady,
    captureCurrentTile,
    removeCurrentTile,
    startStitchPipeline,
    stitchJobId,
    stitchBusy,
    stitchStatus,
    stitchPipelinePercent,
    stitchPipelinePhase,
    stitchPipelineMessage,
    ghostEnabled,
    ghostOpacity,
    setGhostEnabled,
    setGhostOpacity,
    overlapScore,
    overlapHint,
    referenceStripUrl,
    liveStripUrl,
    toast,
    setToast,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="content">
        <CardHeader>
          <CardTitle tag="h5">
            {isStudent ? (
              <MDButton
                style={{ marginBottom: 15, marginRight: 10 }}
                color={"primary"}
                variant="contained"
                onClick={openAiDialog}
              >
                Ask AI
              </MDButton>
            ) : null}
            {canUploadPhotoOnSlide && !activeDeleteButton ? (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {/*
                  ZIP slide upload is hidden while the photo + Lambda
                  pipeline is the canonical path. The hidden <input>
                  + handler are kept so any code that still calls
                  fileInputRef.current.click() (e.g. delete flow)
                  keeps working.
                */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleAddMaterial}
                />
                <input
                  type="file"
                  ref={photoInputRef}
                  accept=".png,.jpg,.jpeg,.tif,.tiff,image/png,image/jpeg,image/tiff"
                  style={{ display: "none" }}
                  onChange={handleUploadPhoto}
                />
                <MDButton
                  style={{ marginBottom: 15 }}
                  color={"secondary"}
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => photoInputRef.current?.click()}
                >
                  Upload Photo
                </MDButton>
              </div>
            ) : canDeletePhotoOnSlide && activeDeleteButton ? (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="file"
                  ref={photoInputRef}
                  accept=".png,.jpg,.jpeg,.tif,.tiff,image/png,image/jpeg,image/tiff"
                  style={{ display: "none" }}
                  onChange={handleUploadPhoto}
                />
                <MDButton
                  style={{ marginBottom: 15 }}
                  color={"primary"}
                  variant="contained"
                  onClick={() => handleAddMaterial()}
                >
                  Delete Slide
                </MDButton>
                <MDButton
                  style={{ marginBottom: 15 }}
                  color={"secondary"}
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => photoInputRef.current?.click()}
                >
                  Upload Photo
                </MDButton>
              </div>
            ) : null}
            {showTranslateAllPins ? (
              <Tooltip
                title={
                  persistedPinCount === 0
                    ? "Add and save pins first, then you can translate them."
                    : drawerOpen
                    ? "Close the pin editor before translating all pins."
                    : "Translate every pin on this slide into all supported app languages. Existing translations are overwritten."
                }
              >
                <span>
                  <MDButton
                    style={{ marginBottom: 15, marginLeft: 10 }}
                    color="info"
                    variant="outlined"
                    startIcon={<TranslateRoundedIcon />}
                    onClick={() => setTranslateAllPinsOpen(true)}
                    disabled={persistedPinCount === 0 || drawerOpen}
                  >
                    Translate all pins
                  </MDButton>
                </span>
              </Tooltip>
            ) : null}
          </CardTitle>
          <ShowTileCaptureLoader
            showLoader={showLoader}
            uploaderTitle={uploaderTitle}
            uploadProgress={uploadProgress}
          />
          {tilingBuildNotice ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                margin: "0 0 12px",
                padding: "10px 14px",
                borderRadius: 8,
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                color: "#1E3A8A",
                fontSize: 14,
                lineHeight: 1.4,
              }}
            >
              <span>{tilingBuildNotice.message}</span>
              <button
                type="button"
                onClick={() => setTilingBuildNotice(null)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#1E40AF",
                  cursor: "pointer",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Dismiss
              </button>
            </div>
          ) : null}
        </CardHeader>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "flex-start",
            gap: 16,
            width: "100%",
          }}
        >
          {showPathologyFeature ? (
            <PathologyRoot
              isAdmin={showPathologyAdmin}
              slideId={id}
              mapRef={mapRef}
              mapReadyVersion={mapReadyVersion}
              geoJson={geoJson}
            >
              <div style={{ flex: 1, width: "100%" }}>
                <ShowTileMapAndDialogs
                  slideId={id}
                  lang={lang}
                  info={info}
                  fetchSlide={fetchSlide}
                  drawerOpen={drawerOpen}
                  toggleDrawer={toggleDrawer}
                  showStudentPinToggle={showStudentPinToggle}
                  studentPinsPanelOpen={studentPinsPanelOpen}
                  setStudentPinsPanelOpen={setStudentPinsPanelOpen}
                  studentsSeePublicPins={studentsSeePublicPins}
                  onStudentsPublicPinsChange={handleStudentsPublicPinsChange}
                  pathologyEnabled
                  dialogStackProps={dialogStackProps}
                />
              </div>
              <div
                style={{
                  width: isMobile ? "100%" : 320,
                  flexShrink: 0,
                  position: isMobile ? "static" : "sticky",
                  top: isMobile ? "auto" : 88,
                  overflow: "visible",
                  zIndex: 2,
                }}
              >
                <PathologySidebarSection
                  compact={!isMobile}
                  minWidth={isMobile ? 0 : 300}
                  maxWidth={isMobile ? 1200 : 320}
                />
                <RelatedModelsPanel
                  sourceTitleId={sourceTitleId}
                  sourceType={resolvedCoreType}
                  isAdmin={String(role || "").toLowerCase() === "admin"}
                  lang={lang}
                  refreshKey={geoJson?.id || 0}
                  compact={!isMobile}
                  minWidth={isMobile ? 0 : 300}
                  maxWidth={isMobile ? 1200 : 320}
                />
              </div>
            </PathologyRoot>
          ) : (
            <>
              <div style={{ flex: 1, width: "100%" }}>
                <ShowTileMapAndDialogs
                  slideId={id}
                  lang={lang}
                  info={info}
                  fetchSlide={fetchSlide}
                  drawerOpen={drawerOpen}
                  toggleDrawer={toggleDrawer}
                  showStudentPinToggle={showStudentPinToggle}
                  studentPinsPanelOpen={studentPinsPanelOpen}
                  setStudentPinsPanelOpen={setStudentPinsPanelOpen}
                  studentsSeePublicPins={studentsSeePublicPins}
                  onStudentsPublicPinsChange={handleStudentsPublicPinsChange}
                  pathologyEnabled={false}
                  dialogStackProps={dialogStackProps}
                />
              </div>
              <div
                style={{
                  width: isMobile ? "100%" : 320,
                  flexShrink: 0,
                  position: isMobile ? "static" : "sticky",
                  top: isMobile ? "auto" : 88,
                }}
              >
                <RelatedModelsPanel
                  sourceTitleId={sourceTitleId}
                  sourceType={resolvedCoreType}
                  isAdmin={String(role || "").toLowerCase() === "admin"}
                  lang={lang}
                  refreshKey={geoJson?.id || 0}
                  compact={!isMobile}
                  minWidth={isMobile ? 0 : 300}
                  maxWidth={isMobile ? 1200 : 320}
                />
              </div>
            </>
          )}
        </div>

      </div>
      <TranslateConfirmDialog
        open={translateAllPinsOpen}
        onClose={() => setTranslateAllPinsOpen(false)}
        title="Translate every pin on this slide"
        description={
          <>
            Every pin currently saved on this{" "}
            {resolvedCoreType === "emb"
              ? "embryology"
              : resolvedCoreType === "rad"
                ? "radiology"
                : "histology"}{" "}
            slide
            will be translated into all supported app languages. Use this after
            labelling a new slide.
          </>
        }
        targetLangsLabel="32 app languages: English, Portuguese (PT/BR), Spanish, Italian, German, Dutch, Chinese (Simplified/Traditional), Japanese, Korean, Arabic, French, Russian, Turkish, Polish, Indonesian, Vietnamese, Hindi, Persian, Romanian, Greek, Czech, Hungarian, Swedish, Danish, Norwegian, Finnish, Malay, Tagalog, Kazakh, Turkmen — source locale is skipped"
        itemSummaryLabel={`${persistedPinCount} pin${persistedPinCount === 1 ? "" : "s"}`}
        confirmLabel="Translate all pins"
        onConfirm={async ({ setStatus }) => {
          setStatus(`Translating ${persistedPinCount} pin(s)…`);
          return translatePinsForSlideCall(id);
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {res?.pinsTranslated ?? 0} of {res?.pinCount ?? 0} pin
              {(res?.pinCount ?? 0) === 1 ? "" : "s"} translated
              {res?.pinsFailed ? ` · ${res.pinsFailed} failed` : ""}
              {res?.pinsSkipped ? ` · ${res.pinsSkipped} skipped (no text)` : ""}
              .
            </Typography>
            {(res?.created > 0 || res?.updated > 0) && (
              <Typography variant="caption" color="text.secondary">
                {res?.created ?? 0} created · {res?.updated ?? 0} updated.
              </Typography>
            )}
          </Stack>
        )}
      />
    </DashboardLayout>
  );
}

const Model = ({ fbxUrl, onLoad }) => {
  const fbx = useLoader(FBXLoader, fbxUrl, (loader) => {
    loader.manager.onLoad = onLoad;
  });

  return <primitive object={fbx} />;
};

export default ShowTile;
